/**
 * DualCTAVideoLanding — Fullscreen Mux video landing with two CTAs.
 *
 * Renders a fullscreen autoplay video from the premium-invite public config
 * (simple_mux_overlay_playback_id) with:
 *   – Primary CTA  : VIP checkout directo (MP AR / Stripe intl) — ver premiumVipCheckoutDestination
 *   – Secondary CTA: Free → Clerk `/registro?plan_tier=free` (cualquier país; free_telegram_url es metadata)
 *
 * Mobile-first. Fully iOS Safari + Instagram in-app browser safe:
 *   • h-dvh / min-h-dvh (no h-screen)
 *   • WebkitTransform translate3d GPU layer (Instagram WebView fixed-positioning)
 *   • touchAction: none + overscrollBehavior: none (iOS rubber-band prevention)
 *   • Safe area insets via max(fallback, env(safe-area-inset-*))
 *   • 44 px minimum touch targets
 *   • instagram-browser-modal class for height cascade in Meta IAB
 *   • Body position:fixed + overflow:hidden to prevent page scroll bleed-through
 *
 * Route: /video-landing (add to App.tsx as needed; also usable on any slug).
 *
 * Config — pulled from /api/premium-invite-public-config at runtime:
 *   simple_mux_overlay_playback_id  — playback ID for the video
 *   free_telegram_url               — secondary free CTA URL
 *   vip_checkout_base_url           — override CTA VIP (Comunidad admin); DualCTA + Reels lo aplican
 *
 * Env var fallbacks (build-time):
 *   VITE_PREMIUM_MUX_ES_VIP         — playback ID fallback
 *   VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL — free Telegram URL fallback
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import {
  DUAL_CTA_TEASER_EXTERNAL_ID,
  DUAL_CTA_TEASER_TITLE,
  getDualCtaTeaserMuxPlaybackIds,
} from "@/lib/muxApi";
import {
  applyInstagramBrowserCSS,
  getMetaInAppBrowserInfo,
  isInstagramBrowser,
  shouldUseLightweightIabVideo,
  getMetaIabCtaBottomInsetCss,
  getMetaIabSoftStallMs,
  getMetaIabAutoMountVideoMs,
  getStoredIabCheckoutUrl,
  openUrlInSystemBrowserFromIab,
} from "@/lib/instagramBrowserDetection";
import {
  trackViewContentMeta,
  trackInitiateCheckoutMeta,
  trackLeadMeta,
} from "@/lib/metaPixelAnalytics";
import { cn } from "@/lib/utils";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  resolvePurchaseEvalMuxOverride,
  shouldAbortConversionSurface,
  useVisitorPurchaseEval,
} from "@/hooks/useVisitorPurchaseEval";
import { useConversionControlNavigation } from "@/hooks/useConversionControlNavigation";
import {
  BRAND_SOURCE_KEY,
  BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE,
  BRAND_POWERED_BY_STRIPE_URL,
  BRAND_MERCADOPAGO_HANDSHAKE_URL,
} from "@/lib/brandConfig";
import { getLabelsForLanguage } from "@/config/bioTemplatePremiumPlans";
import {
  isArgentinaPaymentContext,
  resolveCheckoutCountryCode,
} from "@/lib/resolveCheckoutCountryCode";
import { prefetchBrandRuntimeConfig } from "@/lib/brandRuntimeConfig";
import { CANONICAL_MUX_PLAYBACK_ID } from "@/lib/canonicalMuxPlayback";
import {
  dualCtaWatchMilestonePct,
  resolveDualCtaExitReason,
} from "@/lib/dualCtaExitReason";
import {
  CANONICAL_VIP_PRICE_USD,
  CANONICAL_VIP_STRIPE_PRICE_ID,
  getCanonicalVipPlanFallback,
  SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
  SELECT_VIP_FIRST_MONTH_PROMO_ID,
  resolveExteriorVipPromoId,
  discountPercentForExteriorPromo,
} from "@/lib/canonicalVipMembership";
import {
  buildVipPromoTrackingProps,
  shouldApplyChileHighPurchasingPromo,
} from "@/lib/chileHighPurchasingCities";
import { resolveMuxCtaPlaybackFallback } from "@/lib/runtimeBrandEnv";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
  normalizeDualCtaFromWorker,
} from "@/lib/serverTemplateDetection";
import {
  assignEngagementGateArm,
  fetchDualCtaOpsPublic,
  isIabAutoMountVideoEnabled,
  isIabCheckoutBreakoutEnabled,
  isIabResumeBannerEnabled,
  type DualCtaOpsPublic,
  type EngagementGateArm,
} from "@/lib/engagementGateAssignment";
import {
  clampGateSeconds,
  engagementGateTelemetryProps,
  fetchMuxEngagementGate,
} from "@/lib/muxEngagementGate";
import { DUAL_CTA_HOME_SLOT_KEY } from "@/lib/videoAbSlotAdminUtils";

import { comunidadDualCtaExtraProps } from "@/lib/comunidadPostHog";
import {
  getVipCheckoutGateway,
  hasVipCheckoutCta,
  isPremiumInviteLandingPath,
  resolveDirectVipCheckoutUrl,
  resolvePremiumInviteVipCtaHref,
} from "@/lib/premiumVipCheckoutDestination";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { type SupportedLanguage } from "@/config/videoCTAConfig";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import {
  getSlackFunnelDiagnosticsExtraPostHog,
  getPostHogSlackSessionPropertiesPostHog,
  registerDualCtaExperienceContextPostHog,
  trackStripePayFailurePostHog,
  flushPostHogBeforeNavigation,
} from "@/lib/posthogAnalytics";
import {
  persistComunidadArAttribution,
  resolveVipHrefSource,
} from "@/lib/comunidadArAttribution";
import { captureVisitorJourneyRealtime, getJourneyPayloadForServer } from "@/lib/posthogVisitorJourney";
import { getGalleryEngagementForSlack } from "@/lib/galleryEngagementSignal";
import {
  buildExperienceSnapshot,
  type ExperienceVariantSource,
} from "@/lib/experienceSnapshot";
import { useVideoAbSlot } from "@/lib/useVideoAbSlot";
import { formatDualCtaFirstMonthOffer } from "@/lib/dualCtaPriceDisplay";
import {
  buildDualCtaVipPriceTelemetry,
  type DualCtaVipStripePriceSource,
} from "@/lib/dualCtaVipPriceTelemetry";
import {
  DUAL_CTA_MUX_ANALYTICS_SURFACE,
  DUAL_CTA_RECOVERY_EMAIL_KEY,
  getCheckoutEmailForDualCta,
  markDualCtaConverted,
  mirrorDualCtaEventFromContext,
  persistDualCtaCheckoutEmail,
  postDualCtaHighIntentAbandonment,
  recordDualCtaSessionEnter,
  resolveDualCtaFlow,
  setDualCtaJourneyContext,
  trackDualCtaMuxErrorNoConversion,
} from "@/lib/dualCtaFunnelTracking";
import { buildFreeClerkRegistroUrl } from "@/lib/membershipAccessFlow";
import { isFreeMembershipCountry } from "@/lib/freeMembershipCountry";
import {
  isValidTelegramOutboundUrl,
  normalizePublicOutboundUrl,
} from "@/lib/telegramPublicInviteUrl";
import {
  getClerkUserImperative,
} from "@/lib/clerkGate";
import { fetchPlanPricing } from "@/lib/planPricingCache";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Embedded Checkout: lazy — @stripe/stripe-js + el modal NO entran al bundle
// inicial de la landing; cargan recién cuando el visitante clickea el CTA VIP
// con el flag activo.
// IMPORTANT: `lazy` must be imported before this const (TDZ). Do not place
// `import { lazy } from "react"` below this line — it freezes the whole SPA
// on brand-loader (Index imports DualCTAVideoLanding statically).
const StripeEmbeddedCheckoutModal = lazy(
  () => import("@/components/StripeEmbeddedCheckoutModal"),
);

/**
 * Flag runtime de PostHog (sin rebuild — las VITE_FF_* son inertes en Railway).
 * Rollout gradual + A/B de conversión embedded vs hosted desde PostHog:
 * https://us.posthog.com/project/422754/feature_flags → "dual-cta-embedded-checkout".
 */
function isEmbeddedCheckoutFlagOn(): boolean {
  if (typeof window === "undefined") return false;
  const ph = (
    window as {
      posthog?: { isFeatureEnabled?: (key: string) => boolean | undefined };
    }
  ).posthog;
  try {
    return ph?.isFeatureEnabled?.("dual-cta-embedded-checkout") === true;
  } catch {
    return false;
  }
}

// Minimal client-side shape for Dual CTA Video Template (resolved server-side or synthesized).
// Matches api/lib/vipVideoAssets DualCtaVideoTemplate (selected fields).
// Coherencia: imagen SIEMPRE la fija que diste; mux_playback_id es el video.
interface DualCtaVideoTemplate {
  id: number;
  site?: string;
  name?: string | null;
  mux_playback_id?: string | null;
  preview_image_url?: string | null;
  price_usd?: number | null;
  discount_percent?: number | null;
  discount_duration_months?: number | null;
  stripe_price_id?: string | null;
  enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

// ── Build-time + Worker runtime fallbacks ───────────────────────────────────
// Country-aware desde 2026-07-02 (antes: "ES" fijo — un visitante DE/US veía el
// primer frame del video español hasta que algo async lo pisara). Seguro en
// module load: el Worker inyecta __TEMPLATE_CONFIG__ como primer <script> del
// <head>, antes del bundle, y resolveCheckoutCountryCode es 100% sincrónico
// (respeta ?checkout_country y test_country de dev). "UNKNOWN" cae a "ES" para
// preservar el comportamiento previo en bots / dev sin Worker. Frozen en module
// load a propósito: no puede cambiar entre renders → nunca remonta el player.
const ENV_PLAYBACK_ID = (() => {
  const cc = resolveCheckoutCountryCode(getServerTemplateConfig()?.country);
  return resolveMuxCtaPlaybackFallback(cc === "UNKNOWN" ? "ES" : cc) || null;
})();

const ENV_FREE_TELEGRAM_URL =
  (import.meta.env.VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined)?.trim() ||
  null;

// NOTA 2026-06-14: antes existía un `ENV_PLAYBACK_ID` separado
// pero — tras la limpieza del literal del fork anterior — quedó leyendo de la
// MISMA env var que ENV_PLAYBACK_ID. Resultado: cuando el playbackId venía del
// env el chequeo de label hits `=== ENV_PLAYBACK_ID` primero y
// el evento Slack reportaba `playback_id_source: "hardcoded"` aunque la fuente
// real era la env. Reportes desde BR exactamente con ese mensaje.
//
// Quitado: ahora la única fuente de fallback estable es ENV_PLAYBACK_ID. Si
// la cadena de fallback del país está vacía, no hay video al primer frame y el
// resolver Mux API (getDualCtaTeaserMuxPlaybackIds) seteará muxTeaserId apenas
// responda. Los labels Slack ahora son honestos: env / cache / admin / mux_api.

// ── Sin localStorage cache (decisión 2026-06-14) ─────────────────────────────
// Antes había un cache de last-good config en localStorage para acelerar el
// primer frame en visitas repetidas. Eliminado porque generaba bugs de
// "veo el video viejo aunque cambien la env Vercel" — el localStorage tapaba
// las nuevas envs hasta que el cache expirara o el user limpiara browser.
//
// Resolver ahora es 100% Vercel env + Mux API en cada visita:
//   initial state: ENV_PLAYBACK_ID || ENV_PLAYBACK_ID (también de env)
//   después fetch: Mux API si encuentra asset taggeado dlfcpo
//
// Trade-off aceptado: visita repetida pierde el ~50-200 ms de "warm start"
// del HLS (renderiza el video apenas el browser parsea el JS bundle, que
// hoy es <100 KB y carga rápido). A cambio: cambio en Vercel impacta al
// instante en TODOS los usuarios sin esperar a TTLs ni a limpieza manual.
type CachedConfig = {
  playbackId: string | null;
  freeTelegramUrl: string | null;
};

// Stubs no-op para mantener el resto del código sin reescribir — el resolver
// trata CACHED_CONFIG=null y writeCachedConfig=no-op como "cache deshabilitado".
function writeCachedConfig(_cfg: CachedConfig): void {
  /* no-op: localStorage cache deshabilitado deliberadamente */
}

function clearCachedPlaybackId(): void {
  /* no-op: nada que limpiar — siempre leemos ENV + Mux API fresh */
}

const CACHED_CONFIG: CachedConfig | null = null;

// ── Analytics ────────────────────────────────────────────────────────────────
// Funnel events for this page, matching the names used cross-brand so existing
// PostHog insights/dashboards keep working. Mux playback quality is already tracked
// separately by MuxVideoPlayerWithCTA via `mux_video_event`; these add the page-level
// conversion funnel (view → start → CTA click) that was otherwise missing here.
type DualCtaEvent =
  | "dual_cta_video_viewed"
  | "dual_cta_video_started"
  | "dual_cta_video_ended"
  | "dual_cta_video_premium_click"
  | "dual_cta_video_free_click"
  | "dual_cta_video_error"
  | "dual_cta_page_exit"
  | "dual_cta_video_stalled"
  | "dual_cta_no_action_timeout"
  | "dual_cta_scroll_attempt"
  | "dual_cta_vip_cta_impression"
  | "dual_cta_video_waiting_for_play"
  | "dual_cta_checkout_initiated"
  | "dual_cta_template_used_in_checkout"
  | "dual_cta_embedded_checkout_opened"
  | "dual_cta_embedded_checkout_closed"
  | "dual_cta_embedded_checkout_fallback"
  | "dual_cta_engagement_gate_assigned"
  | "dual_cta_engagement_gate_resolved"
  | "dual_cta_engagement_gate_hit"
  /** Soft email capture before Stripe (recovery enablement). */
  | "dual_cta_email_gate_shown"
  | "dual_cta_email_captured"
  | "dual_cta_email_gate_skipped"
  /** IG breakout → system browser handoff auto-started Stripe. */
  | "dual_cta_iab_handoff_autostart";

/** Environment fields that explain soft Mux failures (HTTP / non-secure IAB). */
function getLandingEnvironmentFields(): Record<string, string | number | boolean> {
  if (typeof window === "undefined") return {};
  const protocol = window.location.protocol || "";
  const isSecure =
    typeof window.isSecureContext === "boolean"
      ? window.isSecureContext
      : protocol === "https:";
  return {
    page_protocol: protocol.replace(":", "") || "unknown",
    is_secure_context: isSecure,
    page_origin: window.location.origin || "",
    // True when page is http: — Mux soft-stall + Clerk digest fail in IG IAB
    // (replays 019f7a5f / 019f7a75). Must surface on Slack.
    insecure_page: protocol === "http:" || isSecure === false,
  };
}

/**
 * Worker geo + Admin GeoSim flags for Slack/PostHog.
 * Production truth for country preview is GeoSim (gst mint + Clean VPN),
 * not bare SPA `?country=XX`. See openAdminCountryGeoSimPreview.
 */
function getDualCtaGeoSlackFields(): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  try {
    const geo = getWorkerGeoAnalyticsProps();
    for (const [k, v] of Object.entries(geo)) {
      if (v === undefined || v === null || v === "") continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        out[k] = v;
      }
    }
  } catch {
    /* config not ready */
  }
  // sessionStorage fallback if edge inject hasn't painted adminGeoSim yet
  // but operator already has an active GeoSim session (toolbar).
  if (!out.admin_geo_simulation && typeof sessionStorage !== "undefined") {
    try {
      const raw = sessionStorage.getItem("admin_geo_sim_session");
      if (raw) {
        const s = JSON.parse(raw) as {
          country?: string;
          province?: string;
          city?: string;
          state?: string;
          neighborhood?: string;
          simulateVpn?: string;
        };
        out.admin_geo_simulation = "true";
        if (s.country) out.simulated_country = String(s.country).toUpperCase().slice(0, 2);
        if (s.province) out.simulated_province = String(s.province);
        if (s.city) out.simulated_city = String(s.city);
        if (s.state) out.simulated_state = String(s.state);
        if (s.neighborhood) out.simulated_neighborhood = String(s.neighborhood);
        if (s.simulateVpn) out.simulated_vpn_mode = String(s.simulateVpn);
      }
    } catch {
      /* private mode / bad json */
    }
  }
  return out;
}

function getPageContext() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    page_url: window.location.href,
    referrer: document.referrer || null,
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_content: params.get("utm_content"),
    screen_w: window.screen.width,
    screen_h: window.screen.height,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    dpr: window.devicePixelRatio ?? null,
    connection_type:
      (
        navigator as Navigator & {
          connection?: { effectiveType?: string };
        }
      ).connection?.effectiveType ?? null,
    ...getLandingEnvironmentFields(),
    ...getDualCtaGeoSlackFields(),
  };
}

/**
 * Disparo real-time a Slack vía /api/track-funnel-failure.
 * Sólo envía kinds que existen en VALID_KINDS del backend
 * (page_enter_dual_cta_landing | cta_click_vip | cta_click_free_telegram |
 *  mux_playback_error). El backend resuelve country desde el header geo si
 * el cliente manda "" (DualCTA es la home mundial sin contexto de país).
 */
function postSlackFunnelDualCta(
  kind:
    | "cta_click_vip"
    | "cta_click_free_telegram"
    | "mux_playback_error"
    | "engagement_dropoff",
  sessionId: string,
  extra: Record<string, string | number | boolean | undefined>,
): void {
  if (typeof window === "undefined") return;
  try {
    // replayNearNow: el deep-link al replay arranca cerca del timestamp actual
    // en vez del inicio absoluto. Útil cuando hay señal accionable AHORA: click
    // (decisión), dropoff (último momento antes de irse), mux error (frame
    // exacto en que rompió). Para page_enter no aplica — el inicio es el evento.
    const replayNearNow =
      kind === "cta_click_vip" ||
      kind === "cta_click_free_telegram" ||
      kind === "engagement_dropoff" ||
      kind === "mux_playback_error";
    const phCtx = getPostHogSlackSessionPropertiesPostHog(
      replayNearNow ? { replayNearNow: true } : undefined,
    );
    const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog(
      replayNearNow ? { replayNearNow: true } : undefined,
    );
    const ts = detectTrafficSource();
    const cleanExtra: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(extra)) {
      if (v === undefined || v === null) continue;
      cleanExtra[k] = v;
    }
    // GeoSim primero: country del Worker simulado gana sobre header real del operador.
    const geoSim = getDualCtaGeoSlackFields();
    const leadCtx =
      kind === "cta_click_vip" || kind === "cta_click_free_telegram"
        ? { ...getJourneyPayloadForServer(), ...getGalleryEngagementForSlack() }
        : {};
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        kind,
        template: "DualCTAVideoLanding",
        country: "", // backend → x-vercel-ip-country (Worker ya inyectó sim country en headers path)
        flow_trace_id: sessionId || undefined,
        page_url: window.location.href,
        extra: {
          channel: ts.channel,
          source: ts.source,
          medium: ts.medium,
          in_app_browser: ts.inAppBrowser || undefined,
          ...(ts.raw.referrer_domain && { referrer: ts.raw.referrer_domain }),
          ...geoSim,
          ...leadCtx,
          ...cleanExtra,
          ...phCtx,
          ...funnelDiag,
        },
      }),
    }).catch(() => {});
  } catch {
    /* never break UX */
  }
}

/** Latest experience snapshot — filled by DualCTAVideoLanding for every capture. */
let dualCtaExperienceBase: Record<string, string | number | boolean | null> = {
  template: "DualCTAVideoLanding",
  template_type: "DualCTAVideoLanding",
  experience_template: "DualCTAVideoLanding",
  experience_component: "DualCTAVideoLanding",
  product_surface: "dual_cta_home",
  site: BRAND_SOURCE_KEY || "delfuocampo",
  source: BRAND_SOURCE_KEY || "delfuocampo",
};

function setDualCtaExperienceBase(
  snap: Record<string, string | number | boolean | null>,
): void {
  dualCtaExperienceBase = { ...dualCtaExperienceBase, ...snap };
}

function trackDualCta(event: DualCtaEvent, props?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const eventProps: Record<string, unknown> = {
    ...dualCtaExperienceBase,
    template: "DualCTAVideoLanding",
    template_type: "DualCTAVideoLanding",
    experience_template: "DualCTAVideoLanding",
    experience_component: "DualCTAVideoLanding",
    // source for brand isolation in PostHog queries (mirrors server captureBrandEvent)
    source: BRAND_SOURCE_KEY,
    site: BRAND_SOURCE_KEY || "delfuocampo",
    // For identification of which template (new admin-created ones)
    ...( (window as any).__dual_cta_vip_asset_id ? { vip_video_asset_id: (window as any).__dual_cta_vip_asset_id } : {} ),
    ...( (window as any).__dual_cta_mux_playback_id ? { mux_playback_id: (window as any).__dual_cta_mux_playback_id } : {} ),
    ...( (window as any).__dual_cta_template_name ? { template_name: (window as any).__dual_cta_template_name } : {} ),
    ...props,
  };

  // PostHog — force recorder on funnel events. Sample/lazy_loading + short
  // desktop bounces (YouTube KR 019fa982…) left Slack with PH session IDs but
  // zero events/snapshots: get_session_id() is local; capture never flushed.
  const ph = (
    window as {
      posthog?: {
        capture: (e: string, p?: object) => void;
        startSessionRecording?: (force?: boolean) => void;
        opt_in_capturing?: () => void;
      };
    }
  ).posthog;
  try {
    if (
      event === "dual_cta_video_viewed" ||
      event === "dual_cta_video_started" ||
      event === "dual_cta_video_premium_click" ||
      event === "dual_cta_video_free_click" ||
      event === "dual_cta_checkout_initiated"
    ) {
      ph?.opt_in_capturing?.();
      ph?.startSessionRecording?.(true);
    }
  } catch {
    /* best-effort */
  }
  ph?.capture?.(event, eventProps);
  // Short-session ground truth: flush viewed/started so bounce still lands in PH.
  if (
    event === "dual_cta_video_viewed" ||
    event === "dual_cta_video_started" ||
    event === "dual_cta_video_premium_click" ||
    event === "dual_cta_video_free_click"
  ) {
    void flushPostHogBeforeNavigation(400);
  }

  // GA4 — mirror every dual-CTA event so the funnel is visible in Google Analytics
  const ga = window.gtag;
  if (typeof ga === "function") {
    // Map event names to GA4-recommended names where possible
    const ga4EventName: string =
      event === "dual_cta_video_premium_click" || event === "dual_cta_checkout_initiated"
        ? "begin_checkout"
        : event === "dual_cta_video_free_click"
          ? "select_content"
          : event === "dual_cta_page_exit"
            ? "page_exit"
            : event;

    ga("event", ga4EventName, {
      event_category: "DualCTAVideoLanding",
      event_label: event,
      page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...eventProps,
    });

    // GA4 conversion event for VIP click (maps to standard begin_checkout)
    if (event === "dual_cta_video_premium_click") {
      ga("event", "conversion_funnel", {
        event_category: "Conversion",
        event_label: "DualCTA — VIP click",
        funnel_step: "vip_cta_click",
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
        ...eventProps,
      });
    }

    // GA4 conversion event for free CTA (espejo del VIP): permite comparar
    // engagement por canal en GA4 sin abrir PostHog. `engagement_time_msec`
    // es el parámetro reservado que GA4 usa nativamente para "Engagement"
    // — incluirlo hace que el click aparezca como sesión engaged en GA4
    // aunque el visitante salga al toque al canal de Telegram externo.
    // `value=0` declara que es conversión sin revenue (vs VIP con ticket).
    if (event === "dual_cta_video_free_click") {
      const propsObj = eventProps;
      const secOnPage = typeof propsObj.seconds_on_page === "number"
        ? (propsObj.seconds_on_page as number)
        : 0;
      ga("event", "conversion_funnel", {
        event_category: "Conversion",
        event_label: "DualCTA — Free Telegram click",
        funnel_step: "free_cta_click",
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
        // GA4 engagement: marcar la sesión como engaged al click
        engagement_time_msec: Math.max(1, secOnPage * 1000),
        value: 0,
        currency: "USD",
        ...propsObj,
      });
      // GA4 standard select_content ya se dispara arriba, pero como conversion
      // (custom event en GA4 admin) sólo este `conversion_funnel` con
      // funnel_step explícito es comparable con el VIP del mismo embudo.
    }
  }

  // Force PostHog replay on premium intent — the SDK sampleRate is 0.3 so this
  // is how we still capture 100% of users who reach the highest-intent step.
  if (event === "dual_cta_video_premium_click") {
    const phForceRec = (
      window as { posthog?: { startSessionRecording?: () => void } }
    ).posthog;
    try {
      phForceRec?.startSessionRecording?.();
    } catch {
      /* no-op — replay opt-in is best-effort */
    }
  }

  mirrorDualCtaEventFromContext(event, eventProps);
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DualCTAVideoLanding({
  slotKey = "dual_cta_landing",
  geoMuxPlaybackId,
  geoMuxSource,
  geoVipHref,
  geoVipLabel,
}: {
  slotKey?: string;
  /** SPA fallback (cta-flags) cuando Worker aún no inyectó dualCtaGeo. */
  geoMuxPlaybackId?: string | null;
  geoMuxSource?: string | null;
  /**
   * VIP MP override por geo AR (community url en cta-por-región).
   * Gana sobre dual_cta.vipHref de país y sobre el VIP global.
   */
  geoVipHref?: string | null;
  /** VIP CTA text por geo (community label). Default i18n: Miembro Exclusivo. */
  geoVipLabel?: string | null;
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  // Clerk: SIN hooks acá — la landing está en los paths con provider diferido
  // (perf mobile, ver src/lib/clerkGate.tsx). El estado de sesión se lee
  // imperativamente AL CLICK vía getClerkUserImperative();
  // si clerk-js aún no cargó se trata como signed-out (el default correcto).
  const { setTemplateName } = useCurrentTemplate();
  const {
    country: workerCountry,
    language: workerLanguage,
    province,
    city: workerCity,
    neighborhood,
  } = useTemplateSelection();
  // Ciudad efectiva: Worker inject + query (?city=) para QA high-purchasing CL.
  const effectiveCity = (() => {
    const fromWorker =
      workerCity?.trim() || getServerTemplateConfig()?.city?.trim() || "";
    if (typeof window === "undefined") return fromWorker;
    try {
      const q = new URLSearchParams(window.location.search).get("city")?.trim();
      return q || fromWorker;
    } catch {
      return fromWorker;
    }
  })();
  // Labels i18n para los CTAs del DualCTA — coherentes con MembresiaPremiumVip /
  // BioTemplatePremium / etc. usando la misma convención "Miembro Exclusivo".
  const L = getLabelsForLanguage(workerLanguage);
  // Normalize Worker language string (e.g. "es-AR", "en-US") to the format
  // MuxVideoPlayerWithCTA accepts. Falls back to "en" for unsupported locales.
  const muxLang = (() => {
    const base = workerLanguage.split("-")[0].toLowerCase();
    const supported: readonly SupportedLanguage[] = ["en", "es", "de", "fr", "it", "pt", "nl", "tr", "ar", "pl", "ja", "ko", "id"];
    return supported.includes(base as SupportedLanguage) ? (base as SupportedLanguage) : "en";
  })();
  const effectiveCountry = resolveCheckoutCountryCode(workerCountry);
  // Config DualCTA por país (mux/label/stripe) coordinada desde /admin/country-template
  // (country_template_config.dual_cta_config → KV → Worker → __TEMPLATE_CONFIG__.dualCta).
  // Override autoritativo: si está seteada, gana sobre la cascada por defecto.
  //
  // Modo preview operador: con ?checkout_country=XX se fetchea la config DualCTA
  // de ESE país del endpoint público y pisa la inyectada — permite ver desde AR
  // exactamente lo que ve un visitante de MX/DE/etc. sin VPN ni debug del Worker.
  const [previewDualCta, setPreviewDualCta] = useState<
    NonNullable<ReturnType<typeof getServerTemplateConfig>>["dualCta"] | null
  >(null);
  const dualCtaCfg = previewDualCta ?? getServerTemplateConfig()?.dualCta;
  const dualCtaCountryMux = dualCtaCfg?.muxPlaybackId?.trim() || "";
  // AR geo-fino (cta-por-región): barrio → ciudad → provincia.
  // Prioridad #1 sobre A/B / país / Comunidad.
  // Prop SPA (cta-flags fresh) gana sobre inject stale post-admin-save.
  const injectedGeoMux =
    getServerTemplateConfig()?.dualCtaGeo?.muxPlaybackId?.trim() || "";
  const dualCtaGeoMux = geoMuxPlaybackId?.trim() || injectedGeoMux || "";
  const dualCtaGeoMuxSource =
    (geoMuxPlaybackId?.trim()
      ? geoMuxSource?.trim()
      : getServerTemplateConfig()?.dualCtaGeo?.source?.trim()) || "";

  const purchaseEval = useVisitorPurchaseEval({
    enabled: effectiveCountry === "AR",
    countryCode: effectiveCountry,
    province,
    city: effectiveCity,
    neighborhood,
    currentMux: dualCtaGeoMux || dualCtaCountryMux || undefined,
  });
  const conversionControl = purchaseEval.control;
  const purchaseEvalMuxOverride = resolvePurchaseEvalMuxOverride(purchaseEval.decision);
  const conversionCtaFirst =
    conversionControl.active &&
    conversionControl.surface === "cta" &&
    conversionControl.suppressVideo;
  // Meta IAB (IG MX/CL ads): always boost VIP visual weight for conversion.
  // Use sync detector (not state) — this runs before metaIabInfo useState below.
  const conversionBoostVip =
    conversionControl.boostVipCta || getMetaInAppBrowserInfo().isMetaInApp;

  // Gallery Kimi → /invitacion-premium (AR): mismo bridge que BaseTemplate.
  useConversionControlNavigation(conversionControl, {
    enabled: effectiveCountry === "AR",
    countryCode: effectiveCountry,
  });

  useEffect(() => {
    if (shouldAbortConversionSurface(purchaseEval.decision)) {
      navigate("/", { replace: true });
    }
  }, [purchaseEval.decision, navigate]);

  // First-month discount + full price display — resolved after slot hook below.
  /** Fuerza re-render cuando /api/public/brand-runtime-config hidrata MUX/MP desde DB. */
  const [brandRuntimeReady, setBrandRuntimeReady] = useState(false);
  /** Fallback Mux country-aware: build Vite → edge HTML → brand-runtime-config API. */
  const brandMuxFallback = useMemo(
    () =>
      resolveMuxCtaPlaybackFallback(effectiveCountry) ||
      ENV_PLAYBACK_ID ||
      null,
    [effectiveCountry, brandRuntimeReady],
  );
  const vipCheckoutGateway = useMemo(
    () => getVipCheckoutGateway(effectiveCountry),
    [effectiveCountry, brandRuntimeReady],
  );
  // Override admin por país (country-template dual_cta.vipHref) gana sobre
  // el override global de Comunidad / premium_invite (vip_checkout_base_url).
  const vipHrefOverride = dualCtaCfg?.vipHref?.trim() || "";
  /** Community geo VIP (cta-por-región url MP) — prop SPA > dualCtaGeo.vipHref > ctaUrl inject. */
  const geoCommunityVipHref = useMemo(() => {
    const fromProp = geoVipHref?.trim() || "";
    if (fromProp && /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(fromProp)) {
      return fromProp;
    }
    const cfg = getServerTemplateConfig();
    const fromGeo = cfg?.dualCtaGeo?.vipHref?.trim() || "";
    if (fromGeo && /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(fromGeo)) {
      return fromGeo;
    }
    if (cfg?.showCommunity) {
      const fromCta = cfg.ctaUrl?.trim() || "";
      if (fromCta && /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(fromCta)) {
        return fromCta;
      }
    }
    return "";
  }, [geoVipHref]);
  /** Override global desde /admin/comunidad → premium_invite_config. */
  const [premiumInviteVipBase, setPremiumInviteVipBase] = useState<string | null>(null);
  const primaryCheckoutHref = useMemo(
    () => {
      const cc = (effectiveCountry || "").toUpperCase();
      // 1) Geo community VIP MP (provincia/ciudad/barrio) — más específico.
      if (cc === "AR" && geoCommunityVipHref) return geoCommunityVipHref;
      // 2) dual_cta.vipHref por país:
      //    - AR: solo MP (un vipHref Stripe/intl no debe pisar MercadoPago Comunidad)
      //    - exterior: ignora MP (evita plan AR en GeoSim MX/US/…)
      if (vipHrefOverride) {
        const isMp =
          /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(vipHrefOverride);
        if (cc === "AR") {
          if (isMp) return vipHrefOverride;
        } else {
          // Exterior: NEVER use dual_cta.vipHref for Stripe/Payment Links.
          // Hard-nav skips create-subscription-checkout → no DELFU80 / price force.
          // Only allow MP URLs (rare misconfig); else fall through to Stripe button.
          if (isMp) {
            /* ignore MP on exterior */
          }
        }
      }
      // 3) AR: VIP global VITE_MP_CHECKOUT_VIP_AR / Comunidad override.
      if (cc === "AR") {
        const fromPremium = resolvePremiumInviteVipCtaHref(
          "AR",
          premiumInviteVipBase,
        );
        if (fromPremium && /mercadopago\.com|preapproval_plan_id=/i.test(fromPremium)) {
          return fromPremium;
        }
        return resolveDirectVipCheckoutUrl("AR");
      }
      // Exterior: resolvePremiumInviteVipCtaHref ya descarta vip_checkout_base_url MP.
      // "" → Stripe button (startVipMembershipVideoCheckout).
      const fromPremium = resolvePremiumInviteVipCtaHref(
        effectiveCountry,
        premiumInviteVipBase,
      );
      if (fromPremium) return fromPremium;
      return resolveDirectVipCheckoutUrl(effectiveCountry);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vipHrefOverride,
      geoCommunityVipHref,
      effectiveCountry,
      brandRuntimeReady,
      premiumInviteVipBase,
    ],
  );
  const vipHrefSource = useMemo(() => {
    const href = primaryCheckoutHref?.trim() || "";
    if (geoCommunityVipHref && href === geoCommunityVipHref.trim()) return "geo_community" as const;
    if (vipHrefOverride && href === vipHrefOverride.trim()) return "country_template" as const;
    if (
      premiumInviteVipBase?.trim() &&
      href &&
      href ===
        (resolvePremiumInviteVipCtaHref("AR", premiumInviteVipBase) || "").trim()
    ) {
      return "premium_invite_config" as const;
    }
    return resolveVipHrefSource({
      geoCommunityVipHref,
      countryVipHref: vipHrefOverride,
      premiumInviteVipBase,
      primaryHref: href,
    });
  }, [
    primaryCheckoutHref,
    geoCommunityVipHref,
    vipHrefOverride,
    premiumInviteVipBase,
  ]);
  // VIP / Membresía exclusiva: SIEMPRE visible si hay gateway (Stripe intl / MP AR / vipHref).
  // hideVipCta del admin se ignora en DualCTA home — el toggle por país es solo free.
  const showVipCta = useMemo(
    () =>
      hasVipCheckoutCta(effectiveCountry) ||
      Boolean(vipHrefOverride) ||
      Boolean(geoCommunityVipHref) ||
      Boolean(premiumInviteVipBase?.trim()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      effectiveCountry,
      brandRuntimeReady,
      vipHrefOverride,
      geoCommunityVipHref,
      premiumInviteVipBase,
    ],
  );
  const vipCtaDestination =
    primaryCheckoutHref ||
    (vipCheckoutGateway === "stripe_subscription" ? "stripe_subscription_vip" : "");

  /** Trust badge bajo el CTA VIP: Stripe (intl) o Mercado Pago (AR / GeoSim AR / MP href). */
  const arPaymentContext = isArgentinaPaymentContext(
    workerCountry,
    effectiveCountry,
  );
  const isArVipMercadoPago =
    showVipCta &&
    ((effectiveCountry || "").toUpperCase() === "AR" ||
      vipCheckoutGateway === "mercadopago" ||
      arPaymentContext ||
      /mercadopago\.com|preapproval_plan_id=/i.test(primaryCheckoutHref || ""));
  const showMercadoPagoTrustBadge = isArVipMercadoPago;
  // Stripe "Powered by" badge removed from Dual CTA (replay UX: ruido visual bajo el VIP;
  // el pago sigue siendo Stripe en checkout). MP logo en AR se mantiene.
  const showStripeTrustBadge = false;

  // ── State: config from API ────────────────────────────────────────────────
  // playbackId: solo el PAS custom del admin (simple_mux_overlay_playback_id).
  // Arranca null — el gate de resolución espera a /api/premium-invite-config
  // antes de montar el player. El mux por país (dualCtaCountryMux) vive fuera
  // de este state y gana en la cadena cuando está seteado.
  // null hasta que /api/premium-invite-config responda — evita montar el player
  // con ENV y luego “pisar” con el PAS custom del admin (flash de otro video).
  const [playbackId, setPlaybackId] = useState<string | null>(
    CACHED_CONFIG?.playbackId || null,
  );
  /** true cuando GET /api/premium-invite-config terminó (éxito o fallo). */
  const [configIsReady, setConfigIsReady] = useState(false);
  // Override admin por país (dual_cta_config): URL Telegram del CTA gratis.
  // Gana sobre config global / cache / env — y el fetch de config no lo pisa.
  const [freeTelegramUrl, setFreeTelegramUrl] = useState<string | null>(
    dualCtaCfg?.freeTelegramUrl || CACHED_CONFIG?.freeTelegramUrl || ENV_FREE_TELEGRAM_URL,
  );
  // Free CTA: admin `freeCtaEnabled` / `dual_cta_free_enabled` controlan el
  // botón Clerk `/registro`. Kill de emergencia: FREE_CTA_PRODUCT_LOCKED=true.
  const FREE_CTA_PRODUCT_LOCKED = false;
  const [globalFreeCtaFlag, setGlobalFreeCtaFlag] = useState<boolean | null>(null);
  const freeCtaEnabled = useMemo(() => {
    if (FREE_CTA_PRODUCT_LOCKED) return false;
    // El alta free es el embudo del tráfico de afuera. Argentina compra por
    // Mercado Pago; ofrecerle el botón la manda a un endpoint que la rechaza.
    if (!isFreeMembershipCountry()) return false;
    if (typeof dualCtaCfg?.freeCtaEnabled === "boolean") {
      return dualCtaCfg.freeCtaEnabled;
    }
    return globalFreeCtaFlag === true;
  }, [dualCtaCfg?.freeCtaEnabled, globalFreeCtaFlag]);
  /** True = free no es opción de ingreso (UI oculta + 1 funnel VIP). */
  const freeCtaLocked = FREE_CTA_PRODUCT_LOCKED || freeCtaEnabled === false;
  /** freeCtaFirst solo reordena si free está realmente visible. */
  const freeCtaFirstActive = Boolean(dualCtaCfg?.freeCtaFirst) && freeCtaEnabled && !freeCtaLocked;

  // ── Preview por país (?checkout_country=XX) ───────────────────────────────
  // Herramienta de operador: fetchea la config DualCTA de ese país (fila
  // específica o DEFAULT) del endpoint público y la aplica como si el Worker
  // la hubiera inyectado. Los visitantes normales nunca llevan el param.
  useEffect(() => {
    let cc = "";
    try {
      cc =
        new URLSearchParams(window.location.search)
          .get("checkout_country")
          ?.trim()
          .toUpperCase() ?? "";
    } catch { /* */ }
    if (!/^[A-Z]{2}$/.test(cc)) return;
    fetch(`/api/country-template-config?_t=${Date.now()}`, {
      headers: { "Cache-Control": "no-cache" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          data: {
            entries?: Array<{
              geo_code?: string;
              enabled?: boolean;
              dual_cta_config?: unknown;
            }>;
          } | null,
        ) => {
          if (!data || !Array.isArray(data.entries)) return;
          const byCode = (code: string) =>
            data.entries?.find(
              (e) =>
                e &&
                e.enabled !== false &&
                String(e.geo_code ?? "").toUpperCase() === code,
            );
          const entry = byCode(cc) ?? byCode("DEFAULT");
          const normalized = normalizeDualCtaFromWorker(entry?.dual_cta_config);
          if (!normalized) return;
          setPreviewDualCta(normalized);
          // Estados sembrados con la config inyectada — re-sembrar con el preview.
          if (normalized.freeTelegramUrl) setFreeTelegramUrl(normalized.freeTelegramUrl);
          // freeCtaEnabled se deriva de dualCtaCfg (preview) vía useMemo — no setState.
        },
      )
      .catch(() => { /* preview best-effort */ });
  }, []);
  // Per-language overrides for the 3 CTA labels (vipLabel, freeLabel, loading).
  // Loaded from fetchPremiumInvitePublicConfig → dual_cta_labels_by_lang.
  const [dualCtaOverrides, setDualCtaOverrides] = useState<{ vipLabel?: string; freeLabel?: string; loading?: string } | null>(null);
  // Prioridad: override por PAÍS (/admin/country-template) > override por idioma
  // (dual_cta_labels_by_lang) > default i18n hardcodeado.
  const resolvedGeoVipLabel = (
    geoVipLabel?.trim() ||
    (getServerTemplateConfig()?.showCommunity
      ? getServerTemplateConfig()?.ctaLabel?.trim()
      : "") ||
    ""
  );
  const label = (key: keyof NonNullable<typeof dualCtaOverrides>, fallback: string) => {
    // Prioridad VIP text: geo community label → A/B slot → país → idioma → Miembro Exclusivo.
    if (key === "vipLabel" && resolvedGeoVipLabel) return resolvedGeoVipLabel;
    if (key === "vipLabel" && slotPlaybackId && slotVariantVipLabel) return slotVariantVipLabel;
    if (key === "vipLabel" && dualCtaCfg?.vipLabel) return dualCtaCfg.vipLabel;
    if (key === "freeLabel" && dualCtaCfg?.freeLabel) return dualCtaCfg.freeLabel;
    if (key === "loading" && dualCtaCfg?.loadingLabel) return dualCtaCfg.loadingLabel;
    return dualCtaOverrides?.[key]?.trim() || fallback;
  };
  // Teaser taggeado en Mux (external_id=teaser). Fallback DESPUÉS del PAS custom
  // del admin (simple_mux_overlay_playback_id) y del slot A/B.
  const [muxTeaserId, setMuxTeaserId] = useState<string | null>(null);
  /** true cuando getDualCtaTeaserMuxPlaybackIds terminó (éxito o fallo). */
  const [muxTeaserResolved, setMuxTeaserResolved] = useState(false);
  // Forces the hardcoded fallback when Mux rejects the resolved id post-mount
  // (canonical message: "This URL or playback-id does not exist…"). Without this,
  // a stale Asset ID stored in admin/cache/env keeps failing for every visitor
  // even though we have a known-good asset baked in. One-way switch on purpose:
  // never flips back, so the fallback's own errors can't loop.
  const [forceFallbackPlaybackId, setForceFallbackPlaybackId] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  /**
   * Engagement mid-roll A/B (orthogonal to multi-Mux).
   * control = full video; gate_hotspot = pause at Mux hotspot / fallback %.
   */
  const [engagementGateArm, setEngagementGateArm] =
    useState<EngagementGateArm>("control");
  const [pauseAtSeconds, setPauseAtSeconds] = useState<number | null>(null);
  /** True when videoEnded came from mid-roll gate (not natural onEnded). */
  const [endedViaEngagementGate, setEndedViaEngagementGate] = useState(false);
  const [opsConfig, setOpsConfig] = useState<DualCtaOpsPublic | null>(null);
  const engagementGateMetaRef = useRef<{
    gate_source: string | null;
    gate_seconds: number | null;
    hotspot_score: number | null;
    mux_total_views: number | null;
    assignment_source: string | null;
    rollout_pct: number | null;
    /** True after we re-resolved gate with player duration (short-teaser clamp). */
    duration_refined: boolean;
  }>({
    gate_source: null,
    gate_seconds: null,
    hotspot_score: null,
    mux_total_views: null,
    assignment_source: null,
    rollout_pct: null,
    duration_refined: false,
  });
  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [posterReady, setPosterReady] = useState(false);
  const [checkoutReturnError, setCheckoutReturnError] = useState<string | null>(null);
  /** Post-VIP-click: keep promo visible while Stripe session is created (CL/exterior dropoff). */
  const [checkoutStarting, setCheckoutStarting] = useState(false);
  /** Feedback breve (sin pulse) si el usuario intenta scrollear (página bloqueada). */
  const [ctaScrollHint, setCtaScrollHint] = useState(false);
  /**
   * Video still buffering (or soft-stall): surface “podés unirte ahora” + soft ring
   * so conversion stays possible while Mux loads (CN/GFW / slow 3G).
   * State was used by effects but useState was dropped → ReferenceError crash (GeoSim).
   */
  const [vipCtaWaitingHint, setVipCtaWaitingHint] = useState(false);

  /**
   * Email local de recovery (solo si ya existe en storage/Clerk o se capturó
   * en otro paso). Nunca bloquea el CTA VIP: Stripe + cron/webhook capturan
   * el mail si lo escriben en Checkout.
   */
  const [dualCtaEmail, setDualCtaEmail] = useState("");
  const [dualCtaEmailCaptured, setDualCtaEmailCaptured] = useState(false);
  /** UX free-join recovery: error inline + loading (sin toast ruidoso). */
  const [freeJoinBusy, setFreeJoinBusy] = useState(false);

  /** Props de checkout/país en cada evento del embudo — diagnóstico CO/IG sin replay. */
  useEffect(() => {
    let cancelled = false;
    void prefetchBrandRuntimeConfig().finally(() => {
      if (!cancelled) setBrandRuntimeReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!checkoutReturnError) return;
    const t = setTimeout(() => setCheckoutReturnError(null), 8000);
    return () => clearTimeout(t);
  }, [checkoutReturnError]);

  // Load persisted Dual CTA email for recovery
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("ph_recovery_email_v1") || sessionStorage.getItem(DUAL_CTA_RECOVERY_EMAIL_KEY);
      if (stored && stored.includes("@") && !dualCtaEmail) {
        setDualCtaEmail(stored.toLowerCase());
      }
    } catch {}
  }, []);

  // When local email changes AND is valid, persist for recovery (silent — no UI block).
  useEffect(() => {
    const email = dualCtaEmail.trim().toLowerCase();
    if (
      !email ||
      dualCtaEmailCaptured ||
      !email.includes("@") ||
      !email.includes(".") ||
      email.length < 5
    ) {
      return;
    }
    const { flowName } = resolveDualCtaFlow(location.pathname);
    const effectiveCountry = resolveCheckoutCountryCode(workerCountry);
    persistDualCtaCheckoutEmail({
      email,
      country: effectiveCountry,
      flowName,
      provider: effectiveCountry === "AR" ? "mercadopago" : "stripe",
      amount: getVipPriceTelemetryFields().vip_full_price_usd || 0,
      currency: effectiveCountry === "AR" ? "ARS" : "USD",
      planTier: "vip",
    });
    setDualCtaEmailCaptured(true);
    if (email !== dualCtaEmail) setDualCtaEmail(email);
    trackDualCta("dual_cta_email_captured", {
      cta_kind: "vip",
      source: "silent_storage",
      detected_country: effectiveCountry,
      dual_cta_session_id: dualCtaSessionIdRef.current,
      ...getPageContext(),
    });
  }, [dualCtaEmail, dualCtaEmailCaptured, location.pathname, workerCountry]);

  // ── Redirect checkout errors (Instagram IAB form POST → cancelUrl) ─────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(location.search);
    const errCode = sp.get("checkout_error")?.trim();
    const errMsg = sp.get("checkout_message")?.trim();
    if (!errCode && !errMsg) return;

    const displayMsg =
      errMsg ||
      (workerLanguage === "en"
        ? "We couldn't start checkout. Please try again."
        : "No pudimos iniciar el pago. Intentá de nuevo.");

    setCheckoutReturnError(displayMsg);
    trackStripePayFailurePostHog({
      stage: "checkout_session_create",
      flow_surface: "dual_cta_video_landing",
      stripe_error_code: errCode || undefined,
      error_message: displayMsg,
      plan_tier: "vip",
      failure_kind: "redirect_return",
    });
    mirrorDualCtaEventFromContext("dual_cta_page_exit", {
      exit_reason: "checkout_return_error",
      checkout_error_code: errCode,
      checkout_error_message: displayMsg,
    });

    try {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("checkout_error");
      clean.searchParams.delete("checkout_message");
      window.history.replaceState({}, "", clean.pathname + clean.search + clean.hash);
    } catch {
      /* best-effort URL cleanup */
    }
  }, [location.search, workerLanguage]);

  // ── Funnel tracking state ─────────────────────────────────────────────────
  const mountTimeRef = useRef<number>(Date.now());
  /** Wall-clock when first `playing` fired (fallback if Mux playhead never reported). */
  const videoStartTimeRef = useRef<number | null>(null);
  /**
   * Max Mux playhead (currentTime) seen via onPlaybackTelemetry.
   * This is the real "how long they watched" signal — not wall-clock
   * (wall-clock overcounts pause / tab background / stalls).
   */
  const maxPlayheadSecondsRef = useRef(0);
  const hasDualCtaVideoStarted = (): boolean =>
    videoStartTimeRef.current !== null || maxPlayheadSecondsRef.current > 0.15;
  /** Last known media duration from Mux player (seconds). */
  const videoDurationSecondsRef = useRef<number | null>(null);
  const ctaClickedRef = useRef<"premium" | "free" | null>(null);
  /** True cuando Stripe/MP redirect arrancó (no confundir con solo click VIP). */
  const checkoutInitiatedRef = useRef(false);
  const highIntentAbandonmentFiredRef = useRef(false);
  const videoErrorCountRef = useRef(0);
  const stallCountRef = useRef(0);
  const noActionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Soft-stall / never-played already posted as `mux_playback_error`.
   * Exit must NOT also fire `engagement_dropoff` “sin convertir” — that user
   * never saw the video so conversion was impossible (false lead for ops).
   */
  const videoNeverPlayableSlackFiredRef = useRef(false);
  /** Evita doble `dual_cta_page_exit` (visibilitychange + pagehide). */
  const pageExitFiredRef = useRef(false);
  // Scroll: señal de "quiere más info / está confundido". Lo usamos en el
  // mensaje de engagement_dropoff a Slack para distinguir "se fue sin mirar"
  // de "se fue mientras buscaba más info" (la segunda es mejor lead).
  const scrollAttemptedRef = useRef(false);
  // % máximo de scroll alcanzado: granularidad fina sobre "intentó scrollear".
  const maxScrollPctRef = useRef(0);
  // Cuántas veces se fue del tab (visibilitychange → hidden). >= 3 = distraído /
  // comparando precios en otro tab. 0 = atención focalizada.
  const tabBlurCountRef = useRef(0);
  // Tiempo hasta la primera interacción (scroll, click, video play). Marcado
  // una sola vez. < 2 s = engagement inmediato; > 10 s = pasivo antes de actuar.
  const firstInteractionAtRef = useRef<number | null>(null);
  // Session id compartido con BioTemplatePremiumSimple para correlación de embudo.
  const dualCtaSessionIdRef = useRef<string>("");
  // Tracking del playback_id resuelto: cuál salió y de dónde vino (mux_api /
  // admin / cache / env / hardcoded). Sin esto Slack mostraba "video VIP"
  // genérico sin saber qué asset estaba viendo el usuario, ni si el fallback
  // se activó. Determinístico: si el array de Mux contiene
  // ENV_PLAYBACK_ID lo elegimos, sin importar el orden.
  // 2026-06-14: eliminado el label "hardcoded" porque colisionaba con "env"
  // (ambos apuntaban a la misma env var VITE_PREMIUM_MUX_ES_VIP). Inicial
  // "env" si la env está set; "none" si está vacía y todavía no resolvió Mux API.
  // Fuentes honestas para Slack/PostHog. `ab_slot` gana aunque el mux coincida
  // con dualCta país (same-Mux A/B funcional). `country_template` = dualCta KV.
  // `admin` se mantiene como alias legacy de country_template en payloads viejos.
  const playbackIdSourceRef = useRef<
    | "mux_api"
    | "admin"
    | "country_template"
    | "ab_slot"
    | "geo_ar"
    | "cache"
    | "env"
    | "none"
  >(ENV_PLAYBACK_ID ? "env" : "none");
  const playbackIdAlternativesRef = useRef<string[]>([]);
  // True desde que el gate de mount del player abrió por primera vez. El teaser
  // de Mux API solo puede ganar la cadena ANTES de esto — un swap post-mount
  // cambia la key del player y produce remount/flash visible.
  const playerMountedRef = useRef(false);
  const [muxPlaybackAlternatives, setMuxPlaybackAlternatives] = useState<
    string[]
  >([]);
  // Playback IDs that already failed (invalid id or demuxer) — rotate through
  // Mux API alternatives before giving up. Typical IG IAB crash:
  // DEMUXER_ERROR_COULD_NOT_PARSE ~20s into pu5Hkn… with no alternate asset.
  const failedPlaybackIdsRef = useRef<Set<string>>(new Set());
  const [playbackIdOverride, setPlaybackIdOverride] = useState<string | null>(
    null,
  );
  // Stable remount counter: only increments on explicit error rotation / force
  // fallback. Prevents the player from remounting (flash) when the async
  // resolution chain (slot, config API, Mux teaser) updates the playback ID
  // after the player already mounted. The key is NOT tied to effectivePlaybackId.
  const [playerRemountKey, setPlayerRemountKey] = useState(0);
  // Embedded Checkout (flag PostHog "dual-cta-embedded-checkout"): client
  // secret de la sesión → monta el modal sobre el video. Los args del último
  // intento quedan en el ref para el fallback hosted si el mount falla.
  const [embeddedCheckoutSecret, setEmbeddedCheckoutSecret] = useState<
    string | null
  >(null);
  const lastVipCheckoutArgsRef = useRef<
    Parameters<typeof startVipMembershipVideoCheckout>[0] | null
  >(null);

  // Unrecoverable after exhausting alternates — poster + CTAs, no frozen frame.
  const [videoBroken, setVideoBroken] = useState(false);
  const videoBrokenRef = useRef(false);
  useEffect(() => {
    videoBrokenRef.current = videoBroken;
  }, [videoBroken]);

  useEffect(() => {
    if (!conversionCtaFirst) return;
    setVideoBroken(true);
    setVideoPlaying(false);
    setVipCtaWaitingHint(false);
  }, [conversionCtaFirst]);

  /** Clerk gratis: solo si free NO está locked (producto/admin). Nunca se pinta locked. */
  const showFreeCtaAfterPlayback =
    !freeCtaLocked && freeCtaEnabled && (videoEnded || videoBroken);
  const showFreeCtaDuringPlayback =
    !freeCtaLocked && freeCtaEnabled && !videoEnded && !videoBroken;
  // Free locked: NO render (ni gris). 1 funnel = VIP only.
  const showFreeCtaDisabled = false;

  // Last-resort para errores de RED con la cadena agotada (típico: alternativas
  // de Mux API nunca cargaron y el env fallback es el mismo asset que falló —
  // ver PostHog dual_cta_mux_error_no_conversion sin rotación previa). Un único
  // retry diferido de la cadena completa: red móvil que se recupera ≫ asset roto.
  // Acotado a 1 para no loopear; mientras espera, videoBroken deja el CTA visible.
  const networkChainRetryCountRef = useRef(0);
  const networkChainRetryTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (networkChainRetryTimerRef.current !== null) {
        window.clearTimeout(networkChainRetryTimerRef.current);
      }
    },
    [],
  );

  // ── Video A/B slot — admin-assigned playback ID variants (per-country via VideoAbSlotsAdmin) ─────────────────
  // Fetch the "dual_cta_landing" slot from the admin panel.
  // Per-country video + subscriber price/label comes from variants with countries[].
  // Slot A/B: when enabled + country match, wins over country mux so multi-playback
  // A/B per country is measurable. Country mux is force-single when A/B has no match.
  const {
    playbackId: slotPlaybackId,
    variantLabel: slotVariantLabel,
    variantIndex: slotVariantIndex,
    variantStripePriceId: slotVariantStripePriceId,
    variantVipLabel: slotVariantVipLabel,
    variantFullPriceUsd: slotVariantFullPriceUsd,
    variantFirstMonthDiscountPercent: slotVariantFirstMonthDiscountPercent,
    variantVipVideoAssetId: slotVariantVipVideoAssetId,
    isReady: slotIsReady,
    configVersion: slotConfigVersion,
  } = useVideoAbSlot(slotKey, (effectiveCountry && effectiveCountry !== "UNKNOWN") ? effectiveCountry : undefined);

  // Variante A/B activa = slot resolvió una opción publicable.
  // Mismo Mux que dualCta país es válido: la función real es precio/label/peso/cupón.
  const slotIsActiveVariant = Boolean(slotIsReady && slotPlaybackId);
  const [vipPlanFromDashboard, setVipPlanFromDashboard] = useState(false);
  const [vipPlanFallback, setVipPlanFallback] = useState<{
    fullPriceUsd: number;
    stripePriceId: string | null;
    productName: string | null;
  }>(() => getCanonicalVipPlanFallback());
  // Template fields resolved from slot/server (must be declared before price chain).
  const [activeTemplate, setActiveTemplate] = useState<DualCtaVideoTemplate | null>(null);

  // ── Dual CTA Video Template asignado POR PAÍS (dual_cta_config.vipVideoAssetId) ──
  // El admin puede asignar un template completo (mux + precio + descuento + stripe)
  // a un país desde /admin/country-template. El Worker inyecta solo el ID; acá lo
  // resolvemos en runtime vía /api/dual-cta-template. Independiente del A/B slot: el
  // slot resuelve su template en el server (useVideoAbSlot); este es el del país.
  const countryVipVideoAssetId = dualCtaCfg?.vipVideoAssetId ?? null;
  const [countryTemplate, setCountryTemplate] = useState<DualCtaVideoTemplate | null>(null);
  // false = todavía esperando el fetch (bloquea el mount del player → sin flash).
  // Arranca resuelto cuando el país no tiene template asignado (nada que esperar).
  const [countryTemplateResolved, setCountryTemplateResolved] = useState<boolean>(
    !countryVipVideoAssetId,
  );
  useEffect(() => {
    if (!countryVipVideoAssetId) {
      setCountryTemplate(null);
      setCountryTemplateResolved(true);
      return;
    }
    let cancelled = false;
    setCountryTemplateResolved(false);
    fetch(`/api/dual-cta-template?id=${countryVipVideoAssetId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { template?: DualCtaVideoTemplate } | null) => {
        if (cancelled) return;
        setCountryTemplate(data?.template ?? null);
      })
      .catch(() => {
        if (!cancelled) setCountryTemplate(null);
      })
      .finally(() => {
        // Resuelto (éxito o fallo) → si falló, el landing cae a la cascada normal.
        if (!cancelled) setCountryTemplateResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [countryVipVideoAssetId]);

  // Valores efectivos del template del país, con DIRECT-WINS: si el país tiene el
  // campo directo seteado (muxPlaybackId/stripePriceId/…), ese gana y el del
  // template no se usa (queda vacío para caer en la cascada de siempre).
  const ctMux = !dualCtaCountryMux ? countryTemplate?.mux_playback_id?.trim() || "" : "";
  const ctStripe = !dualCtaCfg?.stripePriceId
    ? countryTemplate?.stripe_price_id?.trim() || ""
    : "";
  const ctFullUsd =
    dualCtaCfg?.fullPriceUsd == null ? (countryTemplate?.price_usd ?? undefined) : undefined;
  const ctDiscount =
    dualCtaCfg?.firstMonthDiscountPercent == null
      ? (countryTemplate?.discount_percent ?? undefined)
      : undefined;

  // El país tiene un Dual CTA Video Template asignado y todavía no lo resolvimos:
  // bloquear el mount hasta que el fetch termine, así el player NUNCA monta con el
  // fallback para después pisarlo con el mux del template (= flash). Si el fetch
  // falla, countryTemplateResolved queda true y caemos a la cascada normal.
  // Declarado acá (junto a sus deps) porque el funnel "viewed" también lo usa.
  const pendingCountryTemplate =
    Boolean(countryVipVideoAssetId) && !dualCtaCountryMux && !countryTemplateResolved;

  /** Worker-injected mux (dualCta / vip template) — no esperar slot A/B (hasta 3s). */
  const hasEdgeCountryMux = Boolean(dualCtaCountryMux || ctMux);
  /**
   * Slot API puede tardar 3s; el mux por país ya viene en __TEMPLATE_CONFIG__.
   * Montar de inmediato con baseline país; si el slot gana con otro playback,
   * effectivePlaybackId se actualiza sin bloquear el primer play (CO / IG bio).
   */
  const slotResolutionGateOpen =
    slotIsReady || Boolean(dualCtaGeoMux) || hasEdgeCountryMux;

  // Exterior VIP: SIEMPRE price_1TtZs… (prod_UtMb…) para TODAS las variants.
  // A/B solo cambia video/copy — el checkout Stripe no usa price_ de slot/template.
  // AR no usa Stripe (MercadoPago); el id canónico solo afecta copy USD de fallback.
  const isExteriorDualCta = effectiveCountry !== "AR";
  const activeVipStripePriceId = isExteriorDualCta
    ? CANONICAL_VIP_STRIPE_PRICE_ID
    : (activeTemplate?.stripe_price_id) ||
      (slotIsActiveVariant && slotVariantStripePriceId) ||
      dualCtaCfg?.stripePriceId ||
      ctStripe ||
      vipPlanFallback?.stripePriceId ||
      CANONICAL_VIP_STRIPE_PRICE_ID;

  // Cascada video: geo AR fino > slot A/B > país dualCta > country-template > fallback
  const activePlaybackId =
    dualCtaGeoMux ||
    activeTemplate?.mux_playback_id ||
    (slotIsActiveVariant ? slotPlaybackId : null) ||
    dualCtaCountryMux ||
    ctMux ||
    brandMuxFallback;

  const activeVipVideoAssetId = slotIsActiveVariant ? slotVariantVipVideoAssetId : (dualCtaCfg?.vipVideoAssetId ?? null);

  // Expose for tracking functions so all dual_cta events carry the template ID for identification in PostHog / Slack reports
  if (typeof window !== "undefined") {
    (window as any).__dual_cta_vip_asset_id = activeVipVideoAssetId || null;
    (window as any).__dual_cta_mux_playback_id = activePlaybackId || null;
    (window as any).__dual_cta_template_name = activeTemplate?.name || countryTemplate?.name || null;
    // Debug / e2e: variante real del slot en el init (mismo Mux sigue siendo A/B funcional)
    (window as any).__dual_cta_slot_ready = slotIsReady;
    (window as any).__dual_cta_variant_index = slotIsActiveVariant ? slotVariantIndex : -1;
    (window as any).__dual_cta_variant_label = slotIsActiveVariant ? slotVariantLabel || null : null;
    (window as any).__dual_cta_variant_stripe_price_id = slotIsActiveVariant
      ? slotVariantStripePriceId || null
      : null;
  }

  // Derive activeTemplate locally from the enriched slot response (server resolved the template).
  // The useVideoAbSlot already surfaces variant* fields that were populated from template on server.
  useEffect(() => {
    if (!activeVipVideoAssetId) {
      setActiveTemplate(null);
      return;
    }
    // Build a minimal template shape from slot-provided resolved fields (enriched by server)
    // so that downstream code using activeTemplate.* continues to work unchanged.
    // Prefer explicit slot data (which is the enriched one).
    const t: DualCtaVideoTemplate | null = slotIsActiveVariant && slotPlaybackId
      ? {
          id: activeVipVideoAssetId,
          site: BRAND_SOURCE_KEY,
          name: "Slot Template",
          mux_playback_id: slotPlaybackId,
          // imagen SIEMPRE BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE de brandConfig (coherencia)
          preview_image_url: BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE,
          stripe_price_id: slotVariantStripePriceId || undefined,
          price_usd: slotVariantFullPriceUsd ?? undefined,
          discount_percent: slotVariantFirstMonthDiscountPercent ?? undefined,
          enabled: true,
          created_at: "",
          updated_at: "",
        }
      : null;
    setActiveTemplate(t);
  }, [activeVipVideoAssetId, slotIsActiveVariant, slotPlaybackId, slotVariantStripePriceId, slotVariantFullPriceUsd, slotVariantFirstMonthDiscountPercent]);
  /**
   * promo_… efectivo exterior (todos los países no-AR):
   *   Default DELFU80 80% · ops puede alternar legacy 50% (conversion variable).
   *   Server resolveExteriorVipPromoId mirrors this allowlist.
   */
  const usesSelectVipPromo = isExteriorDualCta;
  const activeStripePromotionCodeId = isExteriorDualCta
    ? resolveExteriorVipPromoId(
        opsConfig?.exterior_default_promo_id ||
          dualCtaCfg?.stripePromotionCodeId ||
          SELECT_VIP_FIRST_MONTH_PROMO_ID,
      )
    : undefined;
  // Exterior: % matches resolved promo (80 or 50). AR: admin/MP local.
  const activeFirstMonthDiscountPercent = isExteriorDualCta
    ? discountPercentForExteriorPromo(activeStripePromotionCodeId)
    : (slotIsActiveVariant && slotVariantFirstMonthDiscountPercent) ||
      dualCtaCfg?.firstMonthDiscountPercent ||
      ctDiscount;
  // Exterior money UI must match Stripe list price ($39.99) + DELFU80 ($8).
  // Admin DEFAULT dual_cta fullPriceUsd was 29.99 → showed "$6 primer mes" (replay
  // 019fc4bc…) while checkout charged 80% of 39.99. Never trust admin full for exterior.
  const activeFullPriceUsd = isExteriorDualCta
    ? CANONICAL_VIP_PRICE_USD
    : (slotIsActiveVariant && slotVariantFullPriceUsd) || dualCtaCfg?.fullPriceUsd || ctFullUsd;
  useEffect(() => {
    let cancelled = false;
    // Fallback canónico de DualCTA: tier VIP del dashboard de planes.
    // El operador lo edita en /dashboard/subscripciones/planes.
    // fetchPlanPricing dedupea con el fetch gemelo de MuxVideoPlayerWithCTA
    // (antes: dos requests idénticos en el arranque — replay 019f6c0a…).
    fetchPlanPricing("vip")
      .then((data) => {
        if (cancelled) return null;
        return data;
      })
      .then((data) => {
        if (!data) return;
        if (String(data.currency ?? "usd").trim().toLowerCase() !== "usd") return;
        const cents = Number(data.amount_cents ?? 0);
        if (!Number.isFinite(cents) || cents <= 0) return;
        const priceId =
          typeof data.stripe_price_id === "string" && data.stripe_price_id.startsWith("price_")
            ? data.stripe_price_id
            : null;
        setVipPlanFallback({
          fullPriceUsd: Math.round(cents) / 100,
          stripePriceId: priceId,
          productName:
            typeof data.product_name === "string" && data.product_name.trim().length > 0
              ? data.product_name.trim()
              : null,
        });
        setVipPlanFromDashboard(true);
      })
      .catch(() => {
        /* canonical fallback already in state */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Internacional: SIEMPRE oferta 1er mes (cupón engagement) junto al VIP.
  // Exterior: list $39.99 + % del cupón ops (80 default / 50 alternable).
  const vipPriceOffer = useMemo(() => {
    if (isExteriorDualCta) {
      return formatDualCtaFirstMonthOffer(
        CANONICAL_VIP_PRICE_USD,
        activeFirstMonthDiscountPercent || SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
      );
    }
    const full = activeFullPriceUsd || vipPlanFallback?.fullPriceUsd;
    const disc = activeFirstMonthDiscountPercent;
    if (!full || !disc || disc <= 0) return null;
    return formatDualCtaFirstMonthOffer(full, disc);
  }, [
    isExteriorDualCta,
    activeFullPriceUsd,
    vipPlanFallback,
    activeFirstMonthDiscountPercent,
  ]);

  const vipStripePriceSource = useMemo((): DualCtaVipStripePriceSource => {
    if (slotIsActiveVariant && slotVariantStripePriceId) return "ab_slot";
    if (dualCtaCfg?.stripePriceId) return "country_admin";
    if (vipPlanFromDashboard) return "dashboard";
    if (activeVipStripePriceId === CANONICAL_VIP_STRIPE_PRICE_ID) return "canonical";
    if (activeVipStripePriceId) return "dashboard";
    return "server_lookup";
  }, [
    slotIsActiveVariant,
    slotVariantStripePriceId,
    dualCtaCfg?.stripePriceId,
    vipPlanFromDashboard,
    activeVipStripePriceId,
  ]);

  const getVipPriceTelemetryFields = useCallback(
    (): Record<string, string | number> =>
      buildDualCtaVipPriceTelemetry({
        stripePriceId: activeVipStripePriceId,
        priceSource: vipStripePriceSource,
        fullPriceUsd: activeFullPriceUsd || vipPlanFallback?.fullPriceUsd,
        firstMonthDiscountPercent: activeFirstMonthDiscountPercent,
        stripePromotionCodeId: activeStripePromotionCodeId,
      }),
    [
      activeVipStripePriceId,
      vipStripePriceSource,
      activeFullPriceUsd,
      vipPlanFallback?.fullPriceUsd,
      activeFirstMonthDiscountPercent,
      activeStripePromotionCodeId,
    ],
  );

  const getVipPromoTrackingFields = useCallback(
    (): Record<string, string | boolean | number> =>
      buildVipPromoTrackingProps({
        country: effectiveCountry,
        city: effectiveCity,
        promoId: activeStripePromotionCodeId,
        adminPromoId: dualCtaCfg?.stripePromotionCodeId,
        discountPercent: activeFirstMonthDiscountPercent,
      }),
    [
      effectiveCountry,
      effectiveCity,
      activeStripePromotionCodeId,
      dualCtaCfg?.stripePromotionCodeId,
      activeFirstMonthDiscountPercent,
    ],
  );

  const getDualCtaCheckoutDiagnostics = useCallback(
    (): Record<string, string | number | boolean> => ({
      show_vip_cta: showVipCta,
      effective_country: effectiveCountry,
      worker_country: workerCountry || "UNKNOWN",
      vip_checkout_gateway: vipCheckoutGateway,
      vip_cta_destination: vipCtaDestination,
      free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
      free_cta_locked: freeCtaLocked,
      single_vip_funnel: freeCtaLocked,
      vip_cta_is_stripe_button: vipCheckoutGateway === "stripe_subscription",
      ...getVipPriceTelemetryFields(),
      ...getVipPromoTrackingFields(),
    }),
    [
      showVipCta,
      effectiveCountry,
      workerCountry,
      vipCheckoutGateway,
      vipCtaDestination,
      freeCtaEnabled,
      freeCtaLocked,
      getVipPriceTelemetryFields,
      getVipPromoTrackingFields,
    ],
  );

  // ── Instagram / embedded social browser detection ─────────────────────────
  // Sync on first paint — useEffect would miss IG CSS/classes for ~1 frame and
  // under-detect Meta IAB when UA looks like desktop Chrome + fbclid referrer.
  const [metaIabInfo] = useState(() => getMetaInAppBrowserInfo());
  const embeddedSocialBrowser = metaIabInfo.isMetaInApp;
  /** IG/FB/Threads IAB: poster + CTAs first; Mux only after tap (no autoplay). */
  const lightweightIabVideo = shouldUseLightweightIabVideo(metaIabInfo);
  const isMetaIabIOS = metaIabInfo.isIOS && embeddedSocialBrowser;
  const isMetaIabAndroid = metaIabInfo.isAndroid && embeddedSocialBrowser;
  const didApplyIgCssRef = useRef(false);
  // Lightweight IAB: do not mount Mux until explicit user gesture (no autoplay).
  const [iabUserRequestedPlay, setIabUserRequestedPlay] = useState(false);
  /** Sticky “completar pago en navegador” after IAB breakout if user stays in WebView. */
  const [iabCheckoutResumeUrl, setIabCheckoutResumeUrl] = useState<string | null>(
    null,
  );
  const lightweightIabVideoRef = useRef(lightweightIabVideo);
  lightweightIabVideoRef.current = lightweightIabVideo;
  const iabUserRequestedPlayRef = useRef(iabUserRequestedPlay);
  iabUserRequestedPlayRef.current = iabUserRequestedPlay;

  // Restore sticky checkout resume if breakout left a URL (user still in IG).
  useEffect(() => {
    if (!embeddedSocialBrowser) return;
    if (!isIabResumeBannerEnabled(opsConfig)) {
      setIabCheckoutResumeUrl(null);
      return;
    }
    const stored = getStoredIabCheckoutUrl();
    if (stored) setIabCheckoutResumeUrl(stored);
  }, [embeddedSocialBrowser, opsConfig]);

  useEffect(() => {
    if (!embeddedSocialBrowser) return;
    if (!didApplyIgCssRef.current) {
      didApplyIgCssRef.current = true;
      applyInstagramBrowserCSS();
    }
  }, [embeddedSocialBrowser]);

  // Meta IAB chrome (top/bottom bars) changes visualViewport height — keep shell filled.
  useEffect(() => {
    if (!embeddedSocialBrowser || typeof window === "undefined") return;
    const vv = window.visualViewport;
    const sync = () => {
      try {
        const h = Math.round(vv?.height || window.innerHeight);
        if (h > 0) {
          document.documentElement.style.setProperty("--ig-vv-height", `${h}px`);
        }
      } catch {
        /* ignore */
      }
    };
    sync();
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      vv?.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [embeddedSocialBrowser]);

  // ── Template context ──────────────────────────────────────────────────────
  useEffect(() => {
    setTemplateName("DualCTAVideoLanding");
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    setDualCtaJourneyContext({
      pathname: location.pathname,
      country: effectiveCountry,
    });
  }, [location.pathname, effectiveCountry]);

  // ── Body lock: prevent scroll bleed-through on iOS / Android ─────────────
  useEffect(() => {
    const saved = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = saved.overflow;
      document.body.style.height = saved.height;
      document.body.style.position = saved.position;
      document.body.style.width = saved.width;
    };
  }, []);

  // ── Primary source: the teaser asset tagged in Mux (external_id=teaser) ────
  // Selección determinística cuando Mux devuelve N assets con el mismo
  // external_id. Orden de preferencia entre los devueltos:
  //   1. ENV_PLAYBACK_ID (VITE_PREMIUM_MUX_ES_VIP) — el operador manda desde
  //      Vercel. Si el ID de la env aparece en la lista de Mux, gana.
  //   2. ENV_PLAYBACK_ID — garantía de estabilidad para
  //      visitas legacy cuando la env está vacía.
  //   3. cleaned[0] — primer asset que Mux devolvió (último recurso).
  // Los alternativos quedan en `playbackIdAlternativesRef` para que el
  // operador los vea en Slack/PostHog.
  useEffect(() => {
    getDualCtaTeaserMuxPlaybackIds()
      .then((ids) => {
        const cleaned = ids.map((x) => x?.trim()).filter(Boolean) as string[];
        if (cleaned.length === 0) return; // no tagged asset → keep fallback chain
        // Las alternativas SIEMPRE entran al pool de rotación (recovery por
        // demuxer-error en IG IAB), gane o no el teaser la cadena de resolución.
        playbackIdAlternativesRef.current = cleaned;
        setMuxPlaybackAlternatives(cleaned);
        // El teaser solo puede ganar la cadena ANTES del mount del player: un
        // swap posterior cambia la key → remount/flash visible. Post-mount el
        // usuario se queda con el seed (precio aceptado de "cero flash").
        if (playerMountedRef.current) return;
        const igMode = isInstagramBrowser();
        // IG IAB: skip env id when other tagged assets exist — pu5Hkn (env) often
        // triggers DEMUXER_ERROR_COULD_NOT_PARSE ~20s in Meta WebView; try siblings first.
        const id =
          igMode && cleaned.length > 1
            ? cleaned.find((x) => x !== ENV_PLAYBACK_ID) ?? cleaned[0]
            : ENV_PLAYBACK_ID && cleaned.includes(ENV_PLAYBACK_ID)
              ? ENV_PLAYBACK_ID
              : cleaned[0];
        playbackIdSourceRef.current = "mux_api";
        setMuxTeaserId(id);
        // Persist so the next visit seeds the player instantly with the teaser.
        writeCachedConfig({
          playbackId: id,
          freeTelegramUrl: CACHED_CONFIG?.freeTelegramUrl ?? ENV_FREE_TELEGRAM_URL,
        });
      })
      .catch(() => {
        /* non-fatal: keep config/cache/env/fallback chain */
      })
      .finally(() => {
        setMuxTeaserResolved(true);
      });
  }, []);

  // ── Fetch dynamic config from API ─────────────────────────────────────────
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;

        const pid =
          cfg.simple_mux_overlay_playback_id?.trim() || null;
        // El mux por país (dual_cta_config.muxPlaybackId) gana sobre el global:
        // no pisar el state — contaminaba telemetría (playback_id_at_mount) y el
        // pool de rotación con un video que este país nunca muestra. Mismo guard
        // que free_telegram_url / dual_cta_free_enabled más abajo.
        // FIX flash: NO pisar el playbackId si el player ya montó — cambiaba
        // effectivePlaybackId → key swap → remount → flash visible. El teaser
        // de Mux API ya tiene este guard (playerMountedRef); aplicamos el mismo
        // principio acá. El admin debe usar dual_cta_config.muxPlaybackId por
        // país para overrides de video, no el overlay global post-mount.
        if (pid && !dualCtaGeoMux && !dualCtaCountryMux && !playerMountedRef.current) {
          setPlaybackId(pid);
          // Solo upgrade el source si el actual es menos prioritario.
          // Mux API > admin > cache > env > none. Admin pisa env/none.
          if (
            playbackIdSourceRef.current === "env" ||
            playbackIdSourceRef.current === "none"
          ) {
            playbackIdSourceRef.current = "admin";
          }
        }

        const ftUrl = cfg.free_telegram_url?.trim() || null;
        // El override por país (dual_cta_config.freeTelegramUrl) gana sobre la global.
        if (ftUrl && !dualCtaCfg?.freeTelegramUrl) setFreeTelegramUrl(ftUrl);

        // Override checkout VIP desde /admin/comunidad (vip_checkout_base_url).
        // País dual_cta.vipHref sigue ganando en primaryCheckoutHref.
        setPremiumInviteVipBase(cfg.vip_checkout_base_url?.trim() || null);

        // Global free flag — solo se usa si el país NO tiene freeCtaEnabled boolean
        // (useMemo freeCtaEnabled). País false nunca se pisa con global true.
        if (typeof cfg.dual_cta_free_enabled === "boolean") {
          setGlobalFreeCtaFlag(cfg.dual_cta_free_enabled);
        }

        // Per-language CTA label overrides for this visitor's language.
        if (cfg.dual_cta_labels_by_lang) {
          try {
            const byLang = JSON.parse(cfg.dual_cta_labels_by_lang) as Record<string, { vipLabel?: string; freeLabel?: string; loading?: string }>;
            const overrides = byLang[muxLang] ?? byLang[muxLang.split("-")[0]] ?? null;
            if (overrides) setDualCtaOverrides(overrides);
          } catch { /* non-fatal */ }
        }

        // Persist last-good values so the next visit seeds the player instantly.
        writeCachedConfig({
          playbackId: pid ?? CACHED_CONFIG?.playbackId ?? ENV_PLAYBACK_ID,
          freeTelegramUrl:
            ftUrl ?? CACHED_CONFIG?.freeTelegramUrl ?? ENV_FREE_TELEGRAM_URL,
        });
      })
      .catch(() => {
        // Non-fatal: keep build-time fallbacks
      })
      .finally(() => {
        setConfigIsReady(true);
      });
  }, [muxLang]);

  // ── Funnel: page viewed (fire once, after A/B slot resolves) ─────────────
  // Waits for slotIsReady so abTrackingFields are always populated when the
  // admin has configured variants. Max wait: 3 s (FETCH_TIMEOUT_MS in hook).
  // On slot failure the hook sets isReady=true with playbackId=null, so the
  // event always fires within 3 s — never missed for legitimate page views.
  // Con mux por país el slot no puede ganar → el funnel arranca al instante.
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    // Con template de país sin resolver, esperar: el ID final aún no está fijo.
    if (pendingCountryTemplate) return;
    // Same gate as player: wait for slot so variant fields are real on first event.
    const resolutionReady =
      slotResolutionGateOpen &&
      (Boolean(slotPlaybackId) ||
        Boolean(dualCtaCountryMux) ||
        Boolean(ctMux) ||
        (configIsReady && muxTeaserResolved));
    if (!resolutionReady) return;
    if (viewedFiredRef.current) return;
    viewedFiredRef.current = true;
    trackDualCta("dual_cta_video_viewed", {
      has_playback_id_at_mount: Boolean(
        slotPlaybackId || dualCtaCountryMux || ctMux || playbackId || muxTeaserId,
      ),
      using_fallback_id: !dualCtaGeoMux && !slotPlaybackId && !dualCtaCountryMux && !ctMux && !playbackId,
      has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
      embedded_social_browser: embeddedSocialBrowser,
      lightweight_iab_video: lightweightIabVideo,
      slot_is_ready: slotIsReady,
      // DualCTA CTAs (VIP / free TG) work without the teaser playing.
      conversion_possible: true,
      ...getDualCtaCheckoutDiagnostics(),
      ...abTrackingFields,
      ...getPageContext(),
    });

    // Real-time webhook: qué template + mux ve el usuario (coherencia).
    // Server → Slack (dedup) + PostHog landing_experience_realtime.
    try {
      const workerMux = (dualCtaCountryMux || ctMux || "").trim();
      // Misma cascada que baseResolvedPlaybackId: slot (variante real) primero.
      const effectiveId = (
        slotPlaybackId ||
        dualCtaCountryMux ||
        ctMux ||
        playbackId ||
        muxTeaserId ||
        brandMuxFallback ||
        ENV_PLAYBACK_ID ||
        CANONICAL_MUX_PLAYBACK_ID ||
        ""
      ).trim();
      const denyPrefix = "KcpwA0200";
      const coherent =
        Boolean(effectiveId) &&
        !effectiveId.includes(denyPrefix) &&
        (!workerMux || workerMux === effectiveId);
      const ts = detectTrafficSource();
      void fetch("/api/track-funnel-failure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          kind: "landing_experience_resolved",
          template: "DualCTAVideoLanding",
          country: "",
          flow_trace_id: dualCtaSessionIdRef.current || undefined,
          page_url: window.location.href,
          extra: {
            worker_template: "DualCTAVideoLanding",
            effective_template: "DualCTAVideoLanding",
            worker_mux_playback_id: workerMux || undefined,
            playback_id: effectiveId || undefined,
            playback_id_source: playbackIdSourceRef.current || undefined,
            playback_id_at_mount: effectiveId || undefined,
            coherence_ok: coherent,
            entry_path: location.pathname,
            entry_channel: ts.channel,
            channel: ts.channel,
            source: ts.source,
            medium: ts.medium,
            worker_country: workerCountry || "UNKNOWN",
            checkout_country_effective: effectiveCountry,
            product_surface:
              location.pathname === "/" ? "dual_cta_home" : "dual_cta_video_landing",
            landing_path: location.pathname,
            has_worker_dual_cta: Boolean(workerMux),
            // Solo true si país ganó la cadena (no ab_slot same-Mux).
            ...(slotPlaybackId
              ? {}
              : workerMux
                ? { mux_country_template_active: true }
                : {}),
            free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
            free_cta_locked: freeCtaLocked,
            has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
            using_env_fallback:
              Boolean(brandMuxFallback) && effectiveId === brandMuxFallback,
            ...getVipPriceTelemetryFields(),
            ...abTrackingFields,
            ...getDualCtaGeoSlackFields(),
            ...getPostHogSlackSessionPropertiesPostHog(),
          },
        }),
      }).catch(() => {});
    } catch {
      /* never break UX */
    }

    // Meta Pixel — ViewContent: user landed on the video/membership page
    trackViewContentMeta({
      content_name: "DualCTA Video Landing",
      content_type: "page",
      content_category: "VIP Membership",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotIsReady, configIsReady, muxTeaserResolved, dualCtaCountryMux, pendingCountryTemplate, ctMux]);

  // ── Funnel: VIP CTA impression (rendered in DOM, once per visit) ───────────
  const vipImpressionFiredRef = useRef(false);
  useEffect(() => {
    if (!showVipCta || vipImpressionFiredRef.current) return;
    vipImpressionFiredRef.current = true;
    trackDualCta("dual_cta_vip_cta_impression", {
      embedded_social_browser: embeddedSocialBrowser,
      lightweight_iab_video: lightweightIabVideo,
      iab_tap_to_play_pending:
        lightweightIabVideo && !iabUserRequestedPlay ? true : undefined,
      poster_ready: posterReady,
      video_playing: videoPlaying,
      ...getDualCtaCheckoutDiagnostics(),
      ...getPageContext(),
    });
  }, [
    showVipCta,
    embeddedSocialBrowser,
    posterReady,
    videoPlaying,
    getDualCtaCheckoutDiagnostics,
  ]);

  // ── Funnel: video still buffering — CTA should remain the primary action ───
  // CN (Mux CDN/GFW) + Meta IAB: surface the "join now" hint sooner.
  const videoWaitingFiredRef = useRef(false);
  useEffect(() => {
    if (videoPlaying || videoEnded || videoBroken) {
      setVipCtaWaitingHint(false);
      return;
    }
    const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
    // IG Android/iOS (MX/CL paid): poster-only feels like "no video" — surface join NOW.
    const hintMs = isCn ? 1_200 : embeddedSocialBrowser ? 350 : 2_500;
    const diagMs = isCn ? 1_800 : embeddedSocialBrowser ? 900 : 3_000;
    const hintTimer = window.setTimeout(() => setVipCtaWaitingHint(true), hintMs);
    const diagTimer = window.setTimeout(() => {
      if (videoWaitingFiredRef.current || videoPlaying || videoEnded) return;
      videoWaitingFiredRef.current = true;
      trackDualCta("dual_cta_video_waiting_for_play", {
        seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
        poster_ready: posterReady,
        video_ready: videoReady,
        embedded_social_browser: embeddedSocialBrowser,
        is_china_cdn_likely: isCn,
        conversion_possible: true,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
    }, diagMs);
    return () => {
      window.clearTimeout(hintTimer);
      window.clearTimeout(diagTimer);
    };
  }, [
    videoPlaying,
    videoEnded,
    videoBroken,
    posterReady,
    videoReady,
    embeddedSocialBrowser,
    effectiveCountry,
    getDualCtaCheckoutDiagnostics,
  ]);

  // ── Slack: visitor alerts on home video landing ──────────────────────────
  // Antes este path (la home `/` mundial) era el único embudo sin señal en
  // Slack — el operador no veía actividad hasta que el usuario navegaba a
  // /invitacion-premium. Ahora replicamos el patrón de BioTemplatePremiumSimple:
  //  1) registramos la visita en `premium_invite_views` para que el cron
  //     `slack-premium-invite-visits` la agregue cada 15 min (con país desde
  //     el header geo de Vercel/CF — ver server-side fallback).
  //  2) disparamos un alert real-time `page_enter_dual_cta_landing` una vez
  //     por sesión (sessionStorage flag + dedup 2 min server-side).
  // Reusamos la key `premium_invite_session_id` para que, si el visitante
  // navega a /invitacion-premium después, la sesión se correlacione en el
  // cron y no cuente como dos visitantes separados.
  // Session id + cron aggregator: fire once ASAP (no wait for A/B).
  const sessionBootFiredRef = useRef(false);
  useEffect(() => {
    if (sessionBootFiredRef.current) return;
    if (typeof window === "undefined") return;
    sessionBootFiredRef.current = true;

    let sessionId = "";
    try {
      sessionId = sessionStorage.getItem("premium_invite_session_id")?.trim() || "";
    } catch { /* storage blocked */ }
    if (!sessionId) {
      sessionId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      try { sessionStorage.setItem("premium_invite_session_id", sessionId); } catch { /* */ }
    }
    dualCtaSessionIdRef.current = sessionId;
    recordDualCtaSessionEnter(location.pathname);

    try {
      void fetch("/api/track-premium-invite-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          path: window.location.pathname,
          language:
            (typeof navigator !== "undefined" && navigator.language) || "",
          sessionId,
          heroCta: "dual_cta",
          template: "DualCTAVideoLanding",
        }),
      }).catch(() => {});
    } catch { /* best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time page_enter Slack: wait until the same resolution gate as the
  // player / dual_cta_video_viewed so we never claim "country admin" when A/B
  // slot will win (same-Mux functional A/B was the classic lie).
  const slackEnterFiredRef = useRef(false);
  useEffect(() => {
    if (slackEnterFiredRef.current) return;
    if (typeof window === "undefined") return;
    if (pendingCountryTemplate) return;
    const resolutionReady =
      slotResolutionGateOpen &&
      (Boolean(slotPlaybackId) ||
        Boolean(dualCtaCountryMux) ||
        Boolean(ctMux) ||
        (configIsReady && muxTeaserResolved));
    if (!resolutionReady) return;
    slackEnterFiredRef.current = true;

    const sessionId =
      dualCtaSessionIdRef.current ||
      (() => {
        try {
          return sessionStorage.getItem("premium_invite_session_id")?.trim() || "";
        } catch {
          return "";
        }
      })();
    if (!sessionId) return;

    const slackAlertKey = `dual_cta_slack_alerted:${sessionId}`;
    let alreadyAlerted = false;
    try { alreadyAlerted = Boolean(sessionStorage.getItem(slackAlertKey)); } catch { /* */ }
    if (alreadyAlerted) return;
    try { sessionStorage.setItem(slackAlertKey, "1"); } catch { /* */ }

    try {
      const ts = detectTrafficSource();
      const phCtx = getPostHogSlackSessionPropertiesPostHog();
      const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog();
      const effectiveId = (
        slotPlaybackId ||
        dualCtaCountryMux ||
        ctMux ||
        playbackId ||
        muxTeaserId ||
        brandMuxFallback ||
        ENV_PLAYBACK_ID ||
        ""
      ).trim();
      void fetch("/api/track-funnel-failure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          kind: "page_enter_dual_cta_landing",
          template: "DualCTAVideoLanding",
          country: "",
          flow_trace_id: sessionId,
          page_url: window.location.href,
          extra: {
            channel: ts.channel,
            source: ts.source,
            medium: ts.medium,
            in_app_browser: ts.inAppBrowser || undefined,
            ...(ts.raw.referrer_domain && { referrer: ts.raw.referrer_domain }),
            embedded_social_browser: embeddedSocialBrowser,
            has_playback_id_at_mount: Boolean(effectiveId),
            free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
            free_cta_locked: freeCtaLocked,
            has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
            using_fallback_id: !dualCtaGeoMux && !slotPlaybackId && !dualCtaCountryMux && !ctMux && !playbackId,
            playback_id: effectiveId || undefined,
            playback_id_at_mount: effectiveId || undefined,
            playback_id_source: playbackIdSourceRef.current || undefined,
            playback_id_source_at_mount: playbackIdSourceRef.current || undefined,
            worker_template: "DualCTAVideoLanding",
            worker_mux_playback_id: (dualCtaCountryMux || ctMux || undefined),
            entry_path: location.pathname,
            entry_channel: ts.channel,
            vip_checkout_destination: vipCtaDestination,
            vip_checkout_gateway: vipCheckoutGateway,
            landing_path: location.pathname,
            worker_country: workerCountry || "UNKNOWN",
            checkout_country_effective: effectiveCountry,
            ...getVipPriceTelemetryFields(),
            ...abTrackingFields,
            product_surface: isPremiumInviteLandingPath(location.pathname)
              ? "premium_invite_landing"
              : location.pathname === "/"
                ? "dual_cta_home"
                : "dual_cta_video_landing",
            funnel: isPremiumInviteLandingPath(location.pathname)
              ? "comunidad_ar"
              : undefined,
            shop_region:
              (effectiveCountry || "").toUpperCase() === "AR" ? "AR" : "intl",
            ...getDualCtaGeoSlackFields(),
            ...phCtx,
            ...funnelDiag,
          },
        }),
      }).catch(() => {});
    } catch { /* never break UX */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slotIsReady,
    configIsReady,
    muxTeaserResolved,
    dualCtaCountryMux,
    pendingCountryTemplate,
    ctMux,
    slotPlaybackId,
  ]);

  /**
   * Prefer Mux playhead (max currentTime). Fall back to wall-clock since first play
   * only if telemetry never reported (very short plays / player without timeupdate).
   */
  const getVideoWatchedSeconds = (): number => {
    const playhead = maxPlayheadSecondsRef.current;
    if (playhead > 0.25) return Math.round(playhead);
    if (videoStartTimeRef.current !== null) {
      return Math.max(0, Math.round((Date.now() - videoStartTimeRef.current) / 1000));
    }
    return 0;
  };

  /** 0–100% of video duration reached (playhead / duration). Heuristic if duration unknown. */
  const getVideoMilestonePct = (watchedSec: number, ended: boolean): number =>
    dualCtaWatchMilestonePct(watchedSec, videoDurationSecondsRef.current, ended);

  /** Extra props for PostHog / Slack: watch + duration + measurement source. */
  const getVideoWatchTelemetryFields = (
    watchedSec: number,
    ended: boolean,
  ): Record<string, string | number | boolean> => {
    const playhead = maxPlayheadSecondsRef.current;
    const usedPlayhead = playhead > 0.25;
    const dur = videoDurationSecondsRef.current;
    return {
      video_watched_seconds: watchedSec,
      video_watch_source: usedPlayhead ? "mux_playhead" : "wall_clock",
      ...(dur != null && dur > 0 ? { video_duration_seconds: Math.round(dur) } : {}),
      video_milestone_pct: getVideoMilestonePct(watchedSec, ended),
      ...(playhead > 0
        ? { video_playhead_max_seconds: Math.round(playhead * 10) / 10 }
        : {}),
    };
  };

  // ── Funnel: no-action timeout — user saw the page but did nothing ──────────
  // Fires at 15 s if no CTA click and no video play. Diagnoses "page loaded but
  // nothing happened" cases (video stalled, price too high seen before video, etc.)
  // Meta/IG lightweight: Mux is intentionally NOT mounted until tap — never-play
  // at 15s is expected idle, not a CDN failure (false "15s sin play" Slack spam).
  useEffect(() => {
    noActionTimerRef.current = setTimeout(() => {
      if (ctaClickedRef.current) return; // already converted
      if (pageExitFiredRef.current) return; // ya registramos salida
      if (!viewedFiredRef.current) return; // página aún no lista (slot A/B)
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      const env = getLandingEnvironmentFields();
      const iabAwaitingTap =
        lightweightIabVideoRef.current && !iabUserRequestedPlayRef.current;
      trackDualCta("dual_cta_no_action_timeout", {
        seconds_on_page: secOnPage,
        video_started: hasDualCtaVideoStarted(),
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        lightweight_iab_video: lightweightIabVideoRef.current,
        iab_awaiting_tap_to_play: iabAwaitingTap,
        has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
        vip_cta_impression_fired: vipImpressionFiredRef.current,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
      // Soft-fail Slack only for real stalls. Skip when IG lightweight is waiting
      // for user gesture (poster + "Toca para ver" by design).
      if (
        !hasDualCtaVideoStarted() &&
        !videoBrokenRef.current &&
        !iabAwaitingTap
      ) {
        videoNeverPlayableSlackFiredRef.current = true;
        const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
        const stuckKind = env.insecure_page
          ? "insecure_page_and_video_stuck"
          : isCn
            ? "mux_cdn_region_cn"
            : "video_stuck_not_playing";
        postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
          hero_cta: "dual_cta",
          issue_kind: stuckKind,
          error_message: env.insecure_page
            ? `Video never played after ${secOnPage}s on ${env.page_protocol}:// (insecure_context). Soft-stall — Mux may not fire onError. NOT a conversion dropoff.`
            : isCn
              ? `CN soft-stall: video never played after ${secOnPage}s. Likely Mux CDN/GFW (stream.mux.com), not missing country config. CTAs still usable full-screen.`
              : `Video never played after ${secOnPage}s (no onPlaying). Soft-stall — no fatal Mux onError. NOT a conversion dropoff.`,
          seconds_since_mount: secOnPage,
          video_started: false,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          is_china_cdn_likely: isCn,
          embedded_social_browser: embeddedSocialBrowser,
          surface_dual_cta: true,
          ...env,
          ...getActivePlaybackFields(),
        });
      }
    }, 15_000);

    return () => {
      if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    };
    // Mount-once timer; IAB flags read from refs at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Immediate Slack if landing is HTTP / non-secure (IG link-in-bio bug) ──
  // Worker now 301s to HTTPS, but if a client still lands on http (cache, old
  // edge, non-CF path) we MUST alert — previously only console (Clerk digest)
  // and soft Mux stall, zero Slack.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const env = getLandingEnvironmentFields();
    if (!env.insecure_page) return;
    const key = `dual_cta_insecure_slack:${dualCtaSessionIdRef.current || "x"}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* */ }
    trackDualCta("dual_cta_insecure_context", {
      ...env,
      embedded_social_browser: embeddedSocialBrowser,
      ...getPageContext(),
    });
    postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
      hero_cta: "dual_cta",
      issue_kind: "insecure_page_context",
      error_message:
        `Page loaded over ${env.page_protocol}:// (isSecureContext=${env.is_secure_context}). ` +
        "Mux soft-stall + Clerk digest/origin fail in IG IAB. Expected https://www.",
      seconds_since_mount: 0,
      video_started: false,
      embedded_social_browser: embeddedSocialBrowser,
      surface_dual_cta: true,
      ...env,
      ...getActivePlaybackFields(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Funnel: page exit without conversion ──────────────────────────────────
  useEffect(() => {
    const handleExit = () => {
      if (pageExitFiredRef.current) return;

      // VIP click sin redirect a checkout → casi compra abandonada (Slack + journey).
      if (
        ctaClickedRef.current === "premium" &&
        !checkoutInitiatedRef.current &&
        !highIntentAbandonmentFiredRef.current
      ) {
        const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
        if (secOnPage >= 15) {
          highIntentAbandonmentFiredRef.current = true;
          const videoWatchedSec = getVideoWatchedSeconds();
          postDualCtaHighIntentAbandonment({
            sessionId: dualCtaSessionIdRef.current,
            pathname: location.pathname,
            country: effectiveCountry,
            secondsOnPage: secOnPage,
            videoWatchedSeconds: videoWatchedSec,
            extra: {
              ...getDualCtaCheckoutDiagnostics(),
              ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
              video_ended: videoEnded,
              video_played: videoPlaying,
            },
          });
        }
      }

      if (ctaClickedRef.current) return;
      // No marcar "salida" antes de que el embudo haya registrado la vista
      // (evita page_exit fantasma en tab background / slot A/B lento).
      if (!viewedFiredRef.current) return;
      pageExitFiredRef.current = true;
      if (noActionTimerRef.current) {
        clearTimeout(noActionTimerRef.current);
        noActionTimerRef.current = null;
      }
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      // Mux playhead max (preferred) — real watch time, not wall-clock since play.
      const videoWatchedSec = getVideoWatchedSeconds();
      const watchFields = getVideoWatchTelemetryFields(videoWatchedSec, videoEnded);

      // Diagnose "why didn't they click?" — key signals:
      // - DualCTA VIP (+ free TG) CTAs are always usable without the teaser.
      //   conversion_possible tracks CTA availability, NOT whether video played.
      // - Never saw video → tech soft-stall (mux_playback_error), still convertible.
      // - Watched full video but no click → price concern / lost interest.
      // - Short time → immediate bounce.
      // - Video errors → technical failure; CTAs still full-screen when videoBroken.
      // - Partial watch is duration-aware (src/lib/dualCtaExitReason.ts): short
      //   teasers (~11s) with 5s watch must NOT become "no_video_no_click" /
      //   Slack "video no arrancó" (CO IG Android false positive 2026-07-29).
      const videoStarted = hasDualCtaVideoStarted();
      const neverSawVideo =
        !videoStarted && videoWatchedSec <= 0 && !videoEnded;
      // Single funnel when free locked: only VIP counts as usable conversion CTA.
      const dualCtaCtasUsable = freeCtaLocked
        ? showVipCta
        : showVipCta || freeCtaEnabled;
      const exitReason = resolveDualCtaExitReason({
        videoBroken: videoBrokenRef.current,
        videoStarted,
        videoEnded,
        videoWatchedSec,
        videoDurationSec: videoDurationSecondsRef.current,
        secOnPage,
      });

      trackDualCta("dual_cta_page_exit", {
        exit_reason: exitReason,
        seconds_on_page: secOnPage,
        ...watchFields,
        video_started: videoStarted,
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        // Honest: DualCTA conversion does not require the teaser to play.
        conversion_possible: dualCtaCtasUsable,
        not_a_conversion_dropoff: false,
        dual_cta_ctas_usable: dualCtaCtasUsable,
        video_broken: videoBrokenRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
        vip_cta_impression_fired: vipImpressionFiredRef.current,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });

      if (exitReason === "video_error" && videoErrorCountRef.current > 0) {
        trackDualCtaMuxErrorNoConversion({
          trigger: "page_exit",
          pathname: location.pathname,
          country: effectiveCountry,
          errorCount: videoErrorCountRef.current,
          fatal: true,
          videoStarted,
          secondsSinceMount: secOnPage,
          playbackId: effectivePlaybackId,
          exitReason,
          extra: {
            ...watchFields,
            conversion_possible: dualCtaCtasUsable,
            not_a_conversion_dropoff: false,
            dual_cta_ctas_usable: dualCtaCtasUsable,
            embedded_social_browser: embeddedSocialBrowser,
          },
        });
      }

      // Slack rules for DualCTA exit:
      // 1) Video never played → tech alert (mux_playback_error). CTAs still usable.
      // 2) Soft-stall already fired → do not re-spam on exit.
      // 3) Real engagement (watched something, no click) → engagement_dropoff.
      // 4) video_error with CTAs shown long enough → engagement_dropoff too.
      // 5) immediate_bounce → skip generic dropoff.
      const tti =
        firstInteractionAtRef.current != null
          ? Math.round(
              (firstInteractionAtRef.current - mountTimeRef.current) / 1000,
            )
          : null;
      const dropoffBase = {
        hero_cta: "dual_cta",
        exit_reason: exitReason,
        seconds_on_page: secOnPage,
        ...watchFields,
        video_started: videoStarted,
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        scroll_attempted: scrollAttemptedRef.current,
        max_scroll_pct: maxScrollPctRef.current,
        tab_blur_count: tabBlurCountRef.current,
        ...(tti != null && { time_to_first_interaction_s: tti }),
        embedded_social_browser: embeddedSocialBrowser,
        free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
        free_cta_locked: freeCtaLocked,
        has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
        dual_cta_ctas_usable: dualCtaCtasUsable,
        surface_dual_cta: true,
        brand: BRAND_SOURCE_KEY || "delfuocampo",
        // Precio + promo real (DELFU80 / canónico) para Slack dropoff
        ...getVipPriceTelemetryFields(),
        ...getVipPromoTrackingFields(),
        ...getDualCtaCheckoutDiagnostics(),
        ...getActivePlaybackFields(),
      };

      // IG lightweight + no tap: not a tech failure — poster + VIP were intentional.
      // Read refs (exit effect deps are sparse; state may be stale at fire time).
      const iabIdleNoTap =
        lightweightIabVideoRef.current &&
        !iabUserRequestedPlayRef.current &&
        neverSawVideo;

      if (
        (exitReason === "video_never_played" || neverSawVideo) &&
        exitReason !== "immediate_bounce" &&
        !iabIdleNoTap
      ) {
        if (!videoNeverPlayableSlackFiredRef.current) {
          videoNeverPlayableSlackFiredRef.current = true;
          const env = getLandingEnvironmentFields();
          const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
          postSlackFunnelDualCta(
            "mux_playback_error",
            dualCtaSessionIdRef.current,
            {
              ...dropoffBase,
              issue_kind: env.insecure_page
                ? "insecure_page_and_video_stuck"
                : isCn
                  ? "mux_cdn_region_cn"
                  : "video_stuck_not_playing",
              error_message: isCn
                ? `CN: left without Mux play (${secOnPage}s). CDN/GFW likely — VIP/free CTAs still shown. Not admin misconfig.`
                : `Left without video ever playing (${secOnPage}s). ` +
                  "Teaser stuck — DualCTA CTAs were still usable (not a pure tech dead-end).",
              conversion_possible: dualCtaCtasUsable,
              not_a_conversion_dropoff: false,
              is_china_cdn_likely: isCn,
              ...env,
            },
          );
        }
      } else if (iabIdleNoTap && exitReason !== "immediate_bounce") {
        // Engagement signal only (no "15s sin play" tech title).
        postSlackFunnelDualCta("engagement_dropoff", dualCtaSessionIdRef.current, {
          ...dropoffBase,
          issue_kind: "iab_awaiting_tap_to_play",
          exit_reason: "iab_poster_no_tap_no_click",
          conversion_possible: dualCtaCtasUsable,
          not_a_conversion_dropoff: false,
          lightweight_iab_video: true,
          iab_awaiting_tap_to_play: true,
        });
      } else if (
        exitReason === "video_error" &&
        dualCtaCtasUsable &&
        secOnPage >= 8
      ) {
        postSlackFunnelDualCta("engagement_dropoff", dualCtaSessionIdRef.current, {
          ...dropoffBase,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          video_broken_with_usable_ctas: true,
        });
      } else if (
        exitReason !== "immediate_bounce" &&
        exitReason !== "video_error" &&
        exitReason !== "video_never_played"
      ) {
        postSlackFunnelDualCta("engagement_dropoff", dualCtaSessionIdRef.current, {
          ...dropoffBase,
          conversion_possible: dualCtaCtasUsable,
          not_a_conversion_dropoff: false,
        });
      }
    };

    // visibilitychange covers tab close, app switch on mobile, and most navigations
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Contamos el blur (tab switch). Si vuelve y vuelve a irse, contará
        // múltiples — señal real de multitasking / comparación de precios.
        tabBlurCountRef.current += 1;
        handleExit();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handleExit);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handleExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEnded, embeddedSocialBrowser, freeTelegramUrl]);

  // ── Funnel: scroll attempt — user tried to scroll (wants more info / confused) ──
  // También trackea TTI (primera interacción) y max scroll %.
  useEffect(() => {
    let fired = false;
    const markFirstInteraction = () => {
      if (firstInteractionAtRef.current == null) {
        firstInteractionAtRef.current = Date.now();
      }
    };
    const updateMaxScroll = () => {
      // body lock con position:fixed → scrollY del documento siempre es 0.
      // Mejor proxy: la cantidad acumulada que el usuario movió el dedo / rueda.
      // Aproximamos con scrollTop del scrollable container si existe; si no,
      // usamos window.scrollY como mejor esfuerzo (en mobile sin body lock
      // funciona). Es un best-effort, no número absoluto.
      const sy = window.scrollY || document.documentElement.scrollTop || 0;
      const sh = document.documentElement.scrollHeight - window.innerHeight;
      if (sh > 0) {
        const pct = Math.min(100, Math.max(0, Math.round((sy / sh) * 100)));
        if (pct > maxScrollPctRef.current) maxScrollPctRef.current = pct;
      }
    };
    const handleScroll = () => {
      scrollAttemptedRef.current = true;
      markFirstInteraction();
      updateMaxScroll();
      if (fired || ctaClickedRef.current) return;
      fired = true;
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      trackDualCta("dual_cta_scroll_attempt", {
        seconds_on_page: secOnPage,
        video_started: hasDualCtaVideoStarted(),
        embedded_social_browser: embeddedSocialBrowser,
      });
      setCtaScrollHint(true);
      window.setTimeout(() => setCtaScrollHint(false), 3200);
    };
    const handlePointer = () => markFirstInteraction();
    const handleKey = () => markFirstInteraction();
    window.addEventListener("wheel", handleScroll, { passive: true });
    window.addEventListener("touchmove", handleScroll, { passive: true });
    window.addEventListener("scroll", updateMaxScroll, { passive: true });
    window.addEventListener("pointerdown", handlePointer, { passive: true });
    window.addEventListener("keydown", handleKey, { passive: true });
    return () => {
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      window.removeEventListener("scroll", updateMaxScroll);
      window.removeEventListener("pointerdown", handlePointer);
      window.removeEventListener("keydown", handleKey);
    };
  }, [embeddedSocialBrowser]);

  // Cadena de resolución final (sin flash — el player monta solo cuando resolutionReady).
  // Precedencia (#386 ownership / DualCTAVideoLanding):
  //   1) dualCtaGeoMux — AR geo fino (Worker dualCtaGeo o cta-flags SPA fallback)
  //   2) slotPlaybackId — A/B ON con match de país
  //   3) dualCtaCountryMux — edge inject (country_template_config → KV → Worker)
  //   4) ctMux — vipVideoAssetId del país resuelto por API
  //   5) playbackId — Comunidad / premium-invite overlay
  //   6) muxTeaserId — Mux API teaser
  //   7) brandMuxFallback / CANONICAL
  const baseResolvedPlaybackId =
    purchaseEvalMuxOverride ||
    dualCtaGeoMux ||
    slotPlaybackId ||
    dualCtaCountryMux ||
    ctMux ||
    playbackId ||
    muxTeaserId ||
    brandMuxFallback ||
    CANONICAL_MUX_PLAYBACK_ID;

  // Experience tracking: solo atribuir variante A/B si el slot realmente ganó la cadena.
  const abTrackingFields = (() => {
    const base: Record<string, string | number | boolean> = {
      video_ab_slot_key: slotKey,
      video_ab_slot_enabled: Boolean(slotPlaybackId),
    };
    if (dualCtaGeoMux) {
      base.video_ab_variant_label = dualCtaGeoMuxSource
        ? `geo_${dualCtaGeoMuxSource}`
        : "geo_ar";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "geo_ar";
      if (dualCtaGeoMuxSource) base.dual_cta_geo_source = dualCtaGeoMuxSource;
    } else if (slotPlaybackId) {
      base.video_ab_variant_label = slotVariantLabel || `variant_${slotVariantIndex}`;
      base.video_ab_variant_index = slotVariantIndex;
      base.experience_variant_source = "ab_slot";
    } else if (dualCtaCountryMux || ctMux) {
      base.video_ab_variant_label = "baseline";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "country_template";
    } else if (playbackId) {
      // Comunidad /admin/comunidad → simple_mux_overlay_playback_id.
      base.video_ab_variant_label = "premium_invite_overlay";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "admin_premium_invite";
    } else {
      base.video_ab_variant_label = "baseline";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "env_baseline";
    }
    return base;
  })();

  const markDualCtaVideoStarted = useCallback(
    (source: "playing" | "play" | "playhead") => {
      if (videoStartTimeRef.current === null) {
        videoStartTimeRef.current = Date.now();
        trackDualCta("dual_cta_video_started", {
          using_fallback_id: !playbackId,
          seconds_to_play: Math.round((Date.now() - mountTimeRef.current) / 1000),
          start_signal: source,
          embedded_social_browser: embeddedSocialBrowser,
          ...getDualCtaCheckoutDiagnostics(),
          ...abTrackingFields,
          ...getPageContext(),
        });
        trackViewContentMeta({
          content_name: "Teaser Video Play",
          content_category: "DualCTA Video Landing",
        });
      }
      setVideoPlaying(true);
      setVideoBroken(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embeddedSocialBrowser, playbackId],
  );

  // forceFallback: salta el ID roto → brand/edge/canonical (country-aware).
  // activeTemplate (vip_video_asset) gana cuando el A/B slot lo resolvió en server,
  // PERO dualCtaGeoMux siempre gana al slot (ownership AR geo fino).
  const hardFallbackPlaybackId =
    brandMuxFallback || ENV_PLAYBACK_ID || CANONICAL_MUX_PLAYBACK_ID || "";
  const effectivePlaybackId =
    playbackIdOverride ||
    dualCtaGeoMux ||
    (activeTemplate?.mux_playback_id) ||
    (forceFallbackPlaybackId ? hardFallbackPlaybackId : null) ||
    baseResolvedPlaybackId;

  const hasAuthoritativeMux = Boolean(
    dualCtaGeoMux ||
      dualCtaCountryMux ||
      ctMux ||
      slotPlaybackId ||
      playbackId ||
      muxTeaserId,
  );
  // ENV / brandMux / canónico ya son IDs estables — no bloquear el player por
  // brand-runtime-config (prefetch async). Slot OFF o sin match cae acá sin
  // spinner infinito.
  const hasStablePlaybackFallback = Boolean(
    brandMuxFallback || ENV_PLAYBACK_ID || CANONICAL_MUX_PLAYBACK_ID,
  );

  // Gate de mount: esperamos slot A/B salvo mux edge país o geo AR (sync Worker).
  // Timeout del hook = 3s max; CO/intl con dualCta.muxPlaybackId no debe pagarlo.
  const resolutionReady =
    !pendingCountryTemplate &&
    slotResolutionGateOpen &&
    (Boolean(dualCtaGeoMux) ||
      Boolean(slotPlaybackId) ||
      Boolean(dualCtaCountryMux) ||
      Boolean(ctMux) ||
      (configIsReady &&
        muxTeaserResolved &&
        (hasAuthoritativeMux || hasStablePlaybackFallback || brandRuntimeReady)));
  const playerMountGateOpen = resolutionReady;
  if (playerMountGateOpen && effectivePlaybackId) {
    playerMountedRef.current = true;
  }

  // Gap close: auto-mount muted Mux if user never taps play (~88% waiting_for_play).
  // Admin kill switch / iab_auto_mount_video_enabled can disable.
  useEffect(() => {
    if (!lightweightIabVideo || iabUserRequestedPlay) return;
    if (!playerMountGateOpen || !effectivePlaybackId) return;
    if (!isIabAutoMountVideoEnabled(opsConfig)) return;
    const ms = getMetaIabAutoMountVideoMs(metaIabInfo);
    if (ms == null) return;
    const t = window.setTimeout(() => {
      if (iabUserRequestedPlayRef.current) return;
      setIabUserRequestedPlay(true);
      setVipCtaWaitingHint(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).posthog?.capture?.("dual_cta_iab_auto_mount_video", {
          site: BRAND_SOURCE_KEY,
          source: BRAND_SOURCE_KEY,
          country: effectiveCountry,
          delay_ms: ms,
          platform: metaIabInfo.platform,
        });
      } catch {
        /* */
      }
    }, ms);
    return () => window.clearTimeout(t);
  }, [
    lightweightIabVideo,
    iabUserRequestedPlay,
    playerMountGateOpen,
    effectivePlaybackId,
    metaIabInfo,
    effectiveCountry,
    opsConfig,
  ]);

  // ── Engagement gate A/B (orthogonal to multi-Mux playback A/B) ────────────
  // Admin dual_cta_ops_config → rollout; default 0% = all control.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ops = await fetchDualCtaOpsPublic();
      if (cancelled) return;
      setOpsConfig(ops);
      const { arm, source, rolloutPct } = assignEngagementGateArm(
        DUAL_CTA_HOME_SLOT_KEY,
        ops,
      );
      setEngagementGateArm(arm);
      engagementGateMetaRef.current = {
        ...engagementGateMetaRef.current,
        assignment_source: source,
        rollout_pct: rolloutPct,
      };
      setDualCtaExperienceBase(
        engagementGateTelemetryProps({
          arm,
          assignmentSource: source,
          rolloutPct,
          gateSource: null,
          gateSeconds: null,
          hotspotScore: null,
          muxTotalViews: null,
        }),
      );
      trackDualCta("dual_cta_engagement_gate_assigned", {
        engagement_gate_arm: arm,
        engagement_gate_assignment_source: source,
        engagement_gate_rollout_pct: rolloutPct,
        ops_from_admin: Boolean(ops),
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  // Resolve pauseAtSeconds only for gate_hotspot arm when playback is known.
  useEffect(() => {
    // New playback → allow one duration refine pass again
    engagementGateMetaRef.current.duration_refined = false;
    if (engagementGateArm !== "gate_hotspot") {
      setPauseAtSeconds(null);
      return;
    }
    const pid = String(effectivePlaybackId || "").trim();
    if (pid.length < 10) {
      setPauseAtSeconds(null);
      return;
    }
    const ac = new AbortController();
    const durationHint =
      videoDurationSecondsRef.current != null && videoDurationSecondsRef.current > 0
        ? videoDurationSecondsRef.current
        : null;
    void (async () => {
      const body = await fetchMuxEngagementGate(pid, {
        timeframe: "7:days",
        durationSeconds: durationHint,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;

      const serverDur =
        typeof body?.duration_seconds === "number" && body.duration_seconds > 0
          ? body.duration_seconds
          : null;
      const clampDur = durationHint ?? serverDur;

      let startSec =
        body?.gate?.start_seconds != null && Number.isFinite(body.gate.start_seconds)
          ? body.gate.start_seconds
          : 18;
      startSec = clampGateSeconds(startSec, clampDur) ?? startSec;

      if (serverDur != null && !(durationHint != null && durationHint > 0)) {
        videoDurationSecondsRef.current = serverDur;
      }

      const gateSource = body?.gate?.source ?? "fallback_pct";
      engagementGateMetaRef.current = {
        ...engagementGateMetaRef.current,
        gate_source: gateSource,
        gate_seconds: startSec,
        hotspot_score: body?.gate?.score ?? null,
        mux_total_views: body?.total_views ?? 0,
        duration_refined: clampDur != null,
      };
      setDualCtaExperienceBase(
        engagementGateTelemetryProps({
          arm: "gate_hotspot",
          gateSource,
          gateSeconds: startSec,
          hotspotScore: body?.gate?.score ?? null,
          muxTotalViews: body?.total_views ?? 0,
          assignmentSource: engagementGateMetaRef.current.assignment_source,
          rolloutPct: engagementGateMetaRef.current.rollout_pct,
        }),
      );
      setPauseAtSeconds(startSec);
      trackDualCta("dual_cta_engagement_gate_resolved", {
        playback_id: pid,
        duration_seconds: clampDur,
        ...engagementGateTelemetryProps({
          arm: "gate_hotspot",
          gateSource,
          gateSeconds: startSec,
          hotspotScore: body?.gate?.score ?? null,
          muxTotalViews: body?.total_views ?? 0,
          assignmentSource: engagementGateMetaRef.current.assignment_source,
          rolloutPct: engagementGateMetaRef.current.rollout_pct,
        }),
        mux_engagement_error: body?.error ?? null,
      });
    })();
    return () => ac.abort();
  }, [engagementGateArm, effectivePlaybackId]);

  // ── Soft-stall → full-screen CTAs (Mux never fires onError, esp. CN/GFW / Meta IAB) ───
  // After timeout: videoBroken layout (centred VIP + free TG), stop infinite spin.
  // IG lightweight: Mux is not mounted until tap — do NOT soft-stall or Slack
  // "15s sin play" while still showing "Toca para ver" (CL/MX Instagram bio).
  useEffect(() => {
    if (!playerMountGateOpen) return;
    if (videoPlaying || videoEnded || videoBroken) return;
    if (!effectivePlaybackId) return;
    // Poster-only mode: waiting for gesture is healthy, not a stall.
    if (lightweightIabVideo && !iabUserRequestedPlay) return;
    const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
    // Meta IAB after tap: HLS often never hits `playing` — fail open faster.
    const metaStall = getMetaIabSoftStallMs(metaIabInfo);
    const stallMs = isCn
      ? 8_000
      : metaStall != null
        ? metaStall
        : embeddedSocialBrowser
          ? 5_000
          : 12_000;
    const t = window.setTimeout(() => {
      if (hasDualCtaVideoStarted()) return;
      if (videoBrokenRef.current) return;
      if (ctaClickedRef.current) return;
      // Still waiting on lightweight tap? (state may have changed mid-timer)
      if (lightweightIabVideo && !iabUserRequestedPlay) return;
      setVideoBroken(true);
      setVideoPlaying(false);
      setVipCtaWaitingHint(true);
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      trackDualCta("dual_cta_video_soft_stall_cta_surface", {
        seconds_since_mount: secOnPage,
        stall_ms: stallMs,
        is_china_cdn_likely: isCn,
        conversion_possible: true,
        playback_id: effectivePlaybackId,
        embedded_social_browser: embeddedSocialBrowser,
        lightweight_iab_video: lightweightIabVideo,
        iab_user_requested_play: iabUserRequestedPlay,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
      if (!videoNeverPlayableSlackFiredRef.current) {
        videoNeverPlayableSlackFiredRef.current = true;
        const env = getLandingEnvironmentFields();
        postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
          hero_cta: "dual_cta",
          issue_kind: env.insecure_page
            ? "insecure_page_and_video_stuck"
            : isCn
              ? "mux_cdn_region_cn"
              : "video_stuck_not_playing",
          error_message: isCn
            ? `CN soft-stall → full-screen CTAs after ${secOnPage}s (Mux CDN/GFW likely). Conversion still possible.`
            : `Soft-stall → full-screen CTAs after ${secOnPage}s (no onPlaying). Conversion still possible without teaser.`,
          seconds_since_mount: secOnPage,
          video_started: false,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          is_china_cdn_likely: isCn,
          dual_cta_cta_surfaced_early: true,
          embedded_social_browser: embeddedSocialBrowser,
          surface_dual_cta: true,
          ...env,
          ...getActivePlaybackFields(),
        });
      }
    }, stallMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    playerMountGateOpen,
    effectivePlaybackId,
    videoPlaying,
    videoEnded,
    videoBroken,
    effectiveCountry,
    embeddedSocialBrowser,
    isMetaIabAndroid,
    isMetaIabIOS,
    metaIabInfo,
    lightweightIabVideo,
    iabUserRequestedPlay,
  ]);

  // Experience snapshot → every dual_cta_* event + PostHog super-props (admin hub).
  useEffect(() => {
    const variantSource = (abTrackingFields.experience_variant_source ||
      "unknown") as ExperienceVariantSource;
    const mux =
      (typeof effectivePlaybackId === "string" && effectivePlaybackId.trim()) ||
      dualCtaCountryMux ||
      ENV_PLAYBACK_ID ||
      CANONICAL_MUX_PLAYBACK_ID ||
      "";
    const { productSurface } = resolveDualCtaFlow(location.pathname);
    const isComunidadLanding = isPremiumInviteLandingPath(location.pathname);
    const snap = buildExperienceSnapshot({
      template: "DualCTAVideoLanding",
      component: "DualCTAVideoLanding",
      productSurface,
      workerCountry: workerCountry || undefined,
      effectiveCountry,
      variantSource,
      slotKey,
      slotEnabled: Boolean(abTrackingFields.video_ab_slot_enabled),
      variantLabel: String(abTrackingFields.video_ab_variant_label || "baseline"),
      variantIndex:
        typeof abTrackingFields.video_ab_variant_index === "number"
          ? abTrackingFields.video_ab_variant_index
          : -1,
      muxPlaybackId: mux,
      playbackIdSource: playbackIdSourceRef.current || undefined,
      vipStripePriceId: activeVipStripePriceId || undefined,
      vipPriceOfferLine: vipPriceOffer
        ? `${vipPriceOffer.full} → ${vipPriceOffer.firstMonth}`
        : undefined,
      vipDisplayFullUsd: activeFullPriceUsd ?? undefined,
      vipStripePriceSource: vipStripePriceSource || undefined,
      configVersion: slotConfigVersion || undefined,
      extra: {
        ...getVipPriceTelemetryFields(),
        ...getVipPromoTrackingFields(),
        ...(isComunidadLanding
          ? comunidadDualCtaExtraProps({
              country: effectiveCountry,
              vipCheckoutGateway,
              vipCheckoutOverrideActive: Boolean(premiumInviteVipBase?.trim()),
              freeCtaEnabled,
            })
          : {}),
      },
    });
    setDualCtaExperienceBase(snap);
    registerDualCtaExperienceContextPostHog(snap);
  }, [
    abTrackingFields,
    effectivePlaybackId,
    dualCtaCountryMux,
    workerCountry,
    effectiveCountry,
    slotKey,
    location.pathname,
    activeVipStripePriceId,
    vipPriceOffer,
    activeFullPriceUsd,
    vipStripePriceSource,
    getVipPriceTelemetryFields,
    getVipPromoTrackingFields,
    slotConfigVersion,
    vipCheckoutGateway,
    premiumInviteVipBase,
    freeCtaEnabled,
  ]);

  // Al cambiar el playback resuelto, el poster debe volver a cargar antes de
  // retirar el spinner — evita flash del frame anterior.
  useEffect(() => {
    setPosterReady(false);
  }, [effectivePlaybackId, playerRemountKey]);

  /** Next Mux-tagged teaser not yet marked failed (env/admin ids included). */
  const pickNextPlaybackId = (failedId: string): string | null => {
    failedPlaybackIdsRef.current.add(failedId);
    const seen = new Set<string>();
    const pool: string[] = [];
    const altSource = embeddedSocialBrowser
      ? [
          ...playbackIdAlternativesRef.current.filter((x) => x !== brandMuxFallback),
          ...playbackIdAlternativesRef.current,
        ]
      : playbackIdAlternativesRef.current;
    for (const id of [...altSource, playbackId, brandMuxFallback]) {
      const trimmed = id?.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      pool.push(trimmed);
    }
    return pool.find((id) => !failedPlaybackIdsRef.current.has(id)) ?? null;
  };

  // Trackeo del SOURCE final del playback id efectivo. Decide en cada render
  // qué bucket atribuir al id resuelto (no qué setState se llamó). Orden =
  // cadena: geo AR > slot A/B > país > Comunidad.
  if (dualCtaGeoMux && effectivePlaybackId === dualCtaGeoMux) {
    playbackIdSourceRef.current = "geo_ar";
  } else if (slotPlaybackId && effectivePlaybackId === slotPlaybackId) {
    playbackIdSourceRef.current = "ab_slot";
  } else if (dualCtaCountryMux && effectivePlaybackId === dualCtaCountryMux) {
    playbackIdSourceRef.current = "country_template";
  } else if (ctMux && effectivePlaybackId === ctMux) {
    playbackIdSourceRef.current = "country_template";
  } else if (playbackIdOverride) {
    playbackIdSourceRef.current = playbackIdAlternativesRef.current.includes(
      playbackIdOverride,
    )
      ? "mux_api"
      : brandMuxFallback === playbackIdOverride
        ? "env"
        : "admin";
  } else if (forceFallbackPlaybackId) {
    playbackIdSourceRef.current = "env";
  } else if (muxTeaserId && effectivePlaybackId === muxTeaserId) {
    playbackIdSourceRef.current = "mux_api";
  } else if (playbackId && effectivePlaybackId === playbackId) {
    if (
      playbackIdSourceRef.current !== "admin" &&
      playbackIdSourceRef.current !== "country_template" &&
      playbackIdSourceRef.current !== "ab_slot" &&
      playbackIdSourceRef.current !== "geo_ar"
    ) {
      playbackIdSourceRef.current =
        CACHED_CONFIG?.playbackId === playbackId
          ? "cache"
          : brandMuxFallback === playbackId
            ? "env"
            : "admin";
    }
  } else if (brandMuxFallback && effectivePlaybackId === brandMuxFallback) {
    playbackIdSourceRef.current = "env";
  }

  const handlePremiumCtaClick = useCallback(() => {
    ctaClickedRef.current = "premium";
    markDualCtaConverted();
    if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
    const videoWatchedSec = getVideoWatchedSeconds();
    const ctaDestination = vipCtaDestination;
    trackDualCta("dual_cta_video_premium_click", {
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
      seconds_on_page: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      cta_destination: ctaDestination,
      cta_kind: "vip",
      dual_cta_session_id: dualCtaSessionIdRef.current,
      vip_checkout_gateway: vipCheckoutGateway,
      landing_path: location.pathname,
      flow_surface: isPremiumInviteLandingPath(location.pathname)
        ? "premium_invite_landing"
        : "dual_cta_home",
      detected_country: effectiveCountry,
      ...getVipPriceTelemetryFields(),
      ...getVipPromoTrackingFields(),
      ...getActivePlaybackFields(),
      ...abTrackingFields,
      ...getPageContext(),
    });
    trackInitiateCheckoutMeta({
      content_name: "VIP Membership",
      content_type: "product",
      content_category: isPremiumInviteLandingPath(location.pathname)
        ? "Premium Invite Landing"
        : "DualCTA Video Landing",
    });
    postSlackFunnelDualCta("cta_click_vip", dualCtaSessionIdRef.current, {
      hero_cta: "dual_cta",
      cta_destination: ctaDestination,
      vip_checkout_gateway: vipCheckoutGateway,
      landing_path: location.pathname,
      detected_country: effectiveCountry,
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
      time_on_page_s: secOnPage,
      seconds_to_click: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      surface_dual_cta: true,
      brand: BRAND_SOURCE_KEY || "delfuocampo",
      free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
      free_cta_locked: freeCtaLocked,
      has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
      ...getActivePlaybackFields(),
      ...getVipPriceTelemetryFields(),
      ...getVipPromoTrackingFields(),
      ...getDualCtaCheckoutDiagnostics(),
    });
    // getActivePlaybackFields is defined below but all its reactive deps
    // (effectivePlaybackId, vipCtaDestination, vipCheckoutGateway, location.pathname)
    // are already in this array via direct capture — the function itself is not reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoEnded,
    videoPlaying,
    embeddedSocialBrowser,
    vipCtaDestination,
    vipCheckoutGateway,
    location.pathname,
    effectiveCountry,
  ]);

  const isValidDualCtaEmail = useCallback((raw: string) => {
    const v = raw.trim().toLowerCase();
    return v.length >= 5 && v.includes("@") && v.includes(".");
  }, []);

  /** Stripe VIP checkout (after email resolved). Shared by direct path + soft gate. */
  const startStripeVipCheckout = useCallback(
    (checkoutEmail: string | undefined) => {
      if (checkoutStarting) return;
      // Keep DELFU80 offer on screen while Stripe session is created (CL vip_click_no_checkout).
      setCheckoutStarting(true);

      const preferEmbedded =
        !embeddedSocialBrowser && isEmbeddedCheckoutFlagOn();
      const vipCheckoutArgs: Parameters<typeof startVipMembershipVideoCheckout>[0] = {
        preferEmbedded,
        iabCheckoutBreakoutEnabled: isIabCheckoutBreakoutEnabled(opsConfig),
        countryCode: effectiveCountry,
        pathname: location.pathname,
        languageForPlans: localeForPlansFromMuxLanguage(muxLang),
        email: checkoutEmail || undefined,
        city: effectiveCity || undefined,
        // Exterior Stripe: price canónico + DELFU80INTL (80% 1er mes).
        stripePriceId: activeVipStripePriceId,
        ...(isExteriorDualCta
          ? {
              stripePromotionCodeId: activeStripePromotionCodeId,
              discountRate: (activeFirstMonthDiscountPercent ||
                SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT) / 100,
              discountDurationMonths: 1,
            }
          : {}),
        template: "DualCTAVideoLanding",
        template_type: "DualCTAVideoLanding",
        ...(slotVariantIndex >= 0 ? { dual_variant_id: `slot_${slotVariantIndex}` } : {}),
        ...(activeVipVideoAssetId ? { vip_video_asset_id: activeVipVideoAssetId } : {}),
        playback_id: activePlaybackId || effectivePlaybackId || undefined,
        video_ab_slot_key: slotKey || undefined,
        video_ab_variant_label:
          (abTrackingFields.video_ab_variant_label as string | undefined) ||
          slotVariantLabel ||
          undefined,
        video_ab_variant_index:
          slotVariantIndex >= 0
            ? slotVariantIndex
            : typeof abTrackingFields.video_ab_variant_index === "number"
              ? abTrackingFields.video_ab_variant_index
              : undefined,
        onIntent: ({ gateway }) => {
          checkoutInitiatedRef.current = true;
          markDualCtaConverted();
          const { flowName, productSurface } = resolveDualCtaFlow(location.pathname);
          trackDualCta("dual_cta_checkout_initiated", {
            checkout_gateway: gateway,
            has_checkout_email: Boolean(checkoutEmail),
            cta_kind: "vip",
            dual_cta_session_id: dualCtaSessionIdRef.current,
            flow_name: flowName,
            product_surface: productSurface,
            detected_country: effectiveCountry,
            exterior_promo_visible: isExteriorDualCta,
            first_month_discount_percent:
              activeFirstMonthDiscountPercent ||
              (isExteriorDualCta ? SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT : 0),
            stripe_promotion_code_id: isExteriorDualCta
              ? activeStripePromotionCodeId || SELECT_VIP_FIRST_MONTH_PROMO_ID
              : undefined,
            ...(activeVipVideoAssetId ? { vip_video_asset_id: activeVipVideoAssetId } : {}),
            stripe_price_id:
              activeVipStripePriceId ||
              String(getVipPriceTelemetryFields().vip_stripe_price_id || ""),
            ...getVipPriceTelemetryFields(),
            ...getVipPromoTrackingFields(),
            ...getActivePlaybackFields(),
            ...getDualCtaCheckoutDiagnostics(),
            ...abTrackingFields,
            ...getPageContext(),
          });

          trackDualCta("dual_cta_template_used_in_checkout", {
            vip_video_asset_id: activeVipVideoAssetId,
            mux_playback_id: activePlaybackId,
            template_name: activeTemplate?.name,
            checkout_gateway: gateway,
            has_checkout_email: Boolean(checkoutEmail),
            source: BRAND_SOURCE_KEY,
            flow_name: flowName,
            product_surface: productSurface,
            detected_country: effectiveCountry,
          });
        },
      };
      lastVipCheckoutArgsRef.current = vipCheckoutArgs;
      void startVipMembershipVideoCheckout(vipCheckoutArgs)
        .then((result) => {
          if (result.gateway === "stripe_embedded" && result.clientSecret) {
            setCheckoutStarting(false);
            setEmbeddedCheckoutSecret(result.clientSecret);
            trackDualCta("dual_cta_embedded_checkout_opened", {
              detected_country: effectiveCountry,
              has_checkout_email: Boolean(checkoutEmail),
              exterior_promo_visible: isExteriorDualCta,
              ...getPageContext(),
            });
            return;
          }
          // IAB breakout: user may still be in WebView — sticky resume CTA.
          if (
            result.iabBreakout &&
            result.checkoutUrl &&
            isIabResumeBannerEnabled(opsConfig)
          ) {
            setIabCheckoutResumeUrl(result.checkoutUrl);
            // Clear "starting" so VIP + resume banner stay interactive.
            window.setTimeout(() => setCheckoutStarting(false), 600);
            return;
          }
          if (result.iabBreakout) {
            window.setTimeout(() => setCheckoutStarting(false), 600);
            return;
          }
          if (result.gateway === "fan_fallback") {
            window.setTimeout(() => setCheckoutStarting(false), 1200);
          }
        })
        .catch(() => {
          setCheckoutStarting(false);
        });
    },
    [
      checkoutStarting,
      embeddedSocialBrowser,
      effectiveCountry,
      effectiveCity,
      isExteriorDualCta,
      location.pathname,
      muxLang,
      activeVipStripePriceId,
      activeFirstMonthDiscountPercent,
      activeStripePromotionCodeId,
      activeVipVideoAssetId,
      activePlaybackId,
      effectivePlaybackId,
      slotKey,
      slotVariantLabel,
      getDualCtaCheckoutDiagnostics,
      getVipPriceTelemetryFields,
      getVipPromoTrackingFields,
      abTrackingFields,
      slotVariantIndex,
      opsConfig,
    ],
  );

  // IAB handoff: Safari/Chrome opened with ?start_vip_checkout=1 after IG breakout.
  // Auto-start Stripe outside Meta WebView (fetch works → higher pay completion).
  const handoffCheckoutFiredRef = useRef(false);
  useEffect(() => {
    if (handoffCheckoutFiredRef.current) return;
    if (typeof window === "undefined") return;
    if (checkoutStarting) return;
    if (vipCheckoutGateway !== "stripe_subscription") return;
    if (embeddedSocialBrowser || getMetaInAppBrowserInfo().isMetaInApp) return;

    const sp = new URLSearchParams(location.search);
    if (sp.get("start_vip_checkout") !== "1") return;

    handoffCheckoutFiredRef.current = true;
    const vipEmailRaw = (sp.get("vip_email") || "").trim().toLowerCase();
    const handoffEmail = isValidDualCtaEmail(vipEmailRaw) ? vipEmailRaw : undefined;
    if (handoffEmail) setDualCtaEmail(handoffEmail);

    try {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("start_vip_checkout");
      clean.searchParams.delete("vip_email");
      window.history.replaceState(
        {},
        "",
        clean.pathname + clean.search + clean.hash,
      );
    } catch {
      /* */
    }

    trackDualCta("dual_cta_iab_handoff_autostart", {
      detected_country: effectiveCountry,
      has_checkout_email: Boolean(handoffEmail),
      dual_cta_session_id: dualCtaSessionIdRef.current,
      ...getPageContext(),
    });

    const t = window.setTimeout(() => {
      const clerkEmail =
        getClerkUserImperative()?.primaryEmailAddress?.emailAddress;
      const email =
        handoffEmail ||
        dualCtaEmail ||
        getCheckoutEmailForDualCta(clerkEmail) ||
        undefined;
      startStripeVipCheckout(
        email && isValidDualCtaEmail(email) ? email : undefined,
      );
    }, 450);
    return () => window.clearTimeout(t);
  }, [
    checkoutStarting,
    vipCheckoutGateway,
    embeddedSocialBrowser,
    location.search,
    isValidDualCtaEmail,
    dualCtaEmail,
    effectiveCountry,
    startStripeVipCheckout,
  ]);

  const handlePremiumCtaActivate = useCallback(
    (event: { preventDefault: () => void }) => {
      handlePremiumCtaClick();
      const href = (primaryCheckoutHref || "").trim();
      const hrefIsMp =
        /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(href);
      const cc = (effectiveCountry || "").toUpperCase();

      // AR MP hosted checkout (<a> path): track checkout + attribution before leave.
      // Sin esto dual_cta_checkout_initiated / almost_bought_mp_redirect no existen.
      if (href && hrefIsMp && (cc === "AR" || vipCheckoutGateway === "mercadopago")) {
        event.preventDefault();
        const { flowName, productSurface } = resolveDualCtaFlow(location.pathname);
        const playbackFields = getActivePlaybackFields();
        persistComunidadArAttribution({
          landing_path: location.pathname,
          vip_href_source: vipHrefSource,
          cta_destination: href.slice(0, 500),
          playback_id: String(playbackFields.playback_id || ""),
          playback_id_source: String(playbackFields.playback_id_source || ""),
          dual_cta_geo_source: dualCtaGeoMuxSource || undefined,
          dual_cta_session_id: dualCtaSessionIdRef.current,
        });
        trackDualCta("dual_cta_checkout_initiated", {
          checkout_gateway: "mercadopago",
          vip_checkout_gateway: "mercadopago",
          vip_href_source: vipHrefSource,
          cta_destination: href.slice(0, 500),
          flow_name: flowName,
          product_surface: productSurface,
          detected_country: effectiveCountry,
          landing_path: location.pathname,
          ...playbackFields,
          ...abTrackingFields,
          ...getPageContext(),
        });
        captureVisitorJourneyRealtime({
          eventKind: "almost_bought_mp_redirect",
          funnel: flowName,
          productSurface: "checkout",
          recordStep: "mp_checkout_redirect",
          intentLevel: "almost_bought",
          href: href.slice(0, 500),
          country: effectiveCountry || "AR",
          extra: {
            gateway: "mercadopago",
            plan_tier: "vip",
            vip_href_source: vipHrefSource,
            dual_cta_session_id: dualCtaSessionIdRef.current,
            recovery_eligible: false,
            mp_discount_applied: false,
            ...playbackFields,
          },
        });
        const clerkEmail =
          getClerkUserImperative()?.primaryEmailAddress?.emailAddress;
        const checkoutEmail = dualCtaEmail || getCheckoutEmailForDualCta(clerkEmail);
        if (checkoutEmail?.includes("@")) {
          persistDualCtaCheckoutEmail({
            email: checkoutEmail,
            country: "AR",
            flowName,
            provider: "mercadopago",
            planTier: "vip",
          });
        }
        void flushPostHogBeforeNavigation(350).finally(() => {
          window.location.assign(href);
        });
        return;
      }

      // AR only: geo/admin MP override can hard-nav.
      // Exterior: never hard-nav vipHref — always Stripe subscription API (promo+price).
      if (cc === "AR") {
        const directVipOverride = geoCommunityVipHref || vipHrefOverride;
        if (directVipOverride) {
          const overrideIsMp =
            /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(directVipOverride);
          if (overrideIsMp) {
            event.preventDefault();
            void flushPostHogBeforeNavigation(350).finally(() => {
              window.location.assign(directVipOverride);
            });
            return;
          }
        }
      }
      if (vipCheckoutGateway === "stripe_subscription") {
        event.preventDefault();
        if (checkoutStarting) return;
        // UX: never block VIP → Stripe. Prefill only if we already have a
        // valid email (Clerk / sessionStorage). Otherwise Stripe collects it;
        // webhook expired + capture-stripe-emails cron rescue for recovery.
        const clerkEmail =
          getClerkUserImperative()?.primaryEmailAddress?.emailAddress;
        const raw = (dualCtaEmail || getCheckoutEmailForDualCta(clerkEmail) || "")
          .trim()
          .toLowerCase();
        const checkoutEmail = isValidDualCtaEmail(raw) ? raw : undefined;
        startStripeVipCheckout(checkoutEmail);
      }
    },
    [
      handlePremiumCtaClick,
      checkoutStarting,
      vipHrefOverride,
      geoCommunityVipHref,
      primaryCheckoutHref,
      vipHrefSource,
      dualCtaGeoMuxSource,
      vipCheckoutGateway,
      effectiveCountry,
      location.pathname,
      abTrackingFields,
      dualCtaEmail,
      isValidDualCtaEmail,
      startStripeVipCheckout,
    ],
  );

  // Bundle de fields del video activo para todas las alertas Slack (page_enter,
  // cta_click, engagement_dropoff, mux_playback_error). Centralizado para que
  // todos los eventos lleven el mismo contrato y el operador pueda filtrar por
  // playback_id en Slack/PostHog sin abrir cada call site.
  const getActivePlaybackFields = (): Record<string, string | number | boolean> => {
    const alternatives = playbackIdAlternativesRef.current;
    const idx = alternatives.indexOf(effectivePlaybackId);
    // Contrato Slack/PostHog: fuente + variante + free CTA siempre presentes.
    // mux_country_template_active solo si la cadena ganadora es dualCta país
    // (no si ab_slot ganó con el mismo mux — ownership debe apuntar a A/B).
    const winnerIsCountryMux =
      !slotPlaybackId &&
      Boolean(dualCtaCountryMux || ctMux) &&
      (effectivePlaybackId === dualCtaCountryMux || effectivePlaybackId === ctMux);
    return {
      playback_id: effectivePlaybackId,
      playback_id_short: effectivePlaybackId.slice(-8),
      playback_id_source: playbackIdSourceRef.current,
      playback_id_alternatives_count: alternatives.length,
      ...(idx >= 0 && { playback_id_index: idx }),
      playback_id_is_env_fallback:
        Boolean(brandMuxFallback) && effectivePlaybackId === brandMuxFallback,
      ...(winnerIsCountryMux ? { mux_country_template_active: true } : {}),
      free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
      free_cta_locked: freeCtaLocked,
      has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
      ...abTrackingFields,
      vip_checkout_destination: vipCtaDestination,
      vip_checkout_gateway: vipCheckoutGateway,
      vip_href_source: vipHrefSource,
      ...(dualCtaGeoMuxSource ? { dual_cta_geo_source: dualCtaGeoMuxSource } : {}),
      landing_path: location.pathname,
      ...getVipPriceTelemetryFields(),
    };
  };

  // ── Free CTA: registro Clerk gratis → portal (solo países de afuera) ───
  /** Ruta a /registro free (sin pedir email en la landing). Solo fuera de AR. */
  const buildFreeRegistroUrl = useCallback(
    (email?: string) => {
      const tg =
        freeTelegramUrl?.trim() && isValidTelegramOutboundUrl(freeTelegramUrl)
          ? normalizePublicOutboundUrl(freeTelegramUrl.trim())
          : null;
      return buildFreeClerkRegistroUrl({
        source: "dual_cta",
        freeTelegramUrl: tg,
        email: email || dualCtaEmail || undefined,
      });
    },
    [freeTelegramUrl, dualCtaEmail],
  );

  const handleFreeJoin = useCallback(async (opts?: { fromRecoveryEmail?: boolean }) => {
    // Product lock: free Telegram / registro gratis no se puede usar.
    if (FREE_CTA_PRODUCT_LOCKED || !freeCtaEnabled) return;
    // Tracking idéntico al onClick original del <a> — se preserva en cualquier path
    ctaClickedRef.current = "free";
    markDualCtaConverted();
    if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
    const videoWatchedSec = getVideoWatchedSeconds();
    const watchFields = getVideoWatchTelemetryFields(videoWatchedSec, videoEnded);
    const email = dualCtaEmail.trim().toLowerCase();
    const hasEmail = email.includes("@");

    // Capturar email de recuperación antes de ir al portal (lead + recovery).
    if (hasEmail) {
      const { flowName } = resolveDualCtaFlow(location.pathname);
      const cc = resolveCheckoutCountryCode(workerCountry);
      persistDualCtaCheckoutEmail({
        email,
        country: cc,
        flowName,
        provider: cc === "AR" ? "mercadopago" : "stripe",
        amount: 0,
        currency: cc === "AR" ? "ARS" : "USD",
        planTier: "free",
      });
      setDualCtaEmailCaptured(true);
    }

    trackDualCta("dual_cta_video_free_click", {
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...watchFields,
      seconds_on_page: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      cta_kind: "free",
      free_from_recovery_email: Boolean(opts?.fromRecoveryEmail),
      has_recovery_email: hasEmail,
      dual_cta_session_id: dualCtaSessionIdRef.current,
      ...getActivePlaybackFields(),
      ...getPageContext(),
      ...abTrackingFields,
    });
    trackLeadMeta({
      content_name: opts?.fromRecoveryEmail
        ? "Free Clerk register via recovery"
        : "Free Clerk register",
      content_category: "DualCTA Video Landing",
    });
    postSlackFunnelDualCta(
      "cta_click_free_telegram",
      dualCtaSessionIdRef.current,
      {
        hero_cta: "dual_cta",
        telegram_outbound_host: (() => {
          try { return new URL(freeTelegramUrl ?? "").hostname; } catch { return ""; }
        })(),
        telegram_link_style: (freeTelegramUrl ?? "").includes("/+") ? "join" : "channel",
        video_ended: videoEnded,
        video_played: videoPlaying,
        ...watchFields,
        time_on_page_s: secOnPage,
        clicked_before_video_start: !videoPlaying && !videoEnded,
        clicked_during_video: videoPlaying && !videoEnded,
        clicked_after_video: videoEnded,
        video_error_count: videoErrorCountRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        surface_dual_cta: true,
        free_cta_enabled: freeCtaEnabled && !freeCtaLocked,
        free_cta_locked: freeCtaLocked,
        has_free_telegram: freeCtaEnabled && !freeCtaLocked && Boolean(freeTelegramUrl),
        free_from_recovery_email: Boolean(opts?.fromRecoveryEmail),
        has_recovery_email: hasEmail,
        ...getActivePlaybackFields(),
        ...abTrackingFields,
      },
    );

    // Gate de registro — lectura imperativa de Clerk (sin hooks: el provider
    // está diferido en la landing). clerk-js sin cargar ⇒ signed-out, que es
    // el default correcto para el visitante anónimo mayoritario.
    // Tradeoff aceptado (review 2026-07-16): en '/' clerk-js NO carga nunca
    // (provider diferido hasta navegar), así que un miembro ya logueado que
    // toca el CTA free igual va a /registro — la mayoría es anónima y
    // /registro con sesión Clerk activa redirige.
    // Siempre funnel Clerk (/registro → completar → free-join en servidor).
    // Nunca /api/free-join inline: evita exigir free_telegram_url en la landing
    // (AR /invitacion-premium y DualCTA intl comparten este handler).
    navigate(buildFreeRegistroUrl(email));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    freeTelegramUrl,
    navigate,
    videoEnded,
    videoPlaying,
    embeddedSocialBrowser,
    slotPlaybackId,
    slotVariantLabel,
    slotVariantIndex,
    dualCtaEmail,
    buildFreeRegistroUrl,
    workerCountry,
    location.pathname,
  ]);

  /** Free CTA: Clerk registro gratis (sin email en la landing). */
  const handleFreeJoinClick = useCallback(() => {
    if (freeJoinBusy) return;
    setFreeJoinBusy(true);
    void handleFreeJoin().finally(() => {
      setFreeJoinBusy(false);
    });
  }, [freeJoinBusy, handleFreeJoin]);

  return (
    <div
      data-ph-section="dual_cta_landing"
      data-ph-section-label="Dual CTA Video Landing"
      className={cn(
        "fixed inset-0 z-0 w-full h-dvh min-h-dvh overflow-hidden bg-black",
        embeddedSocialBrowser && "instagram-browser-modal instagram-browser-container",
      )}
      data-dual-cta="1"
      data-meta-iab={embeddedSocialBrowser ? "1" : undefined}
      data-meta-iab-platform={
        embeddedSocialBrowser
          ? isMetaIabIOS
            ? "ios"
            : isMetaIabAndroid
              ? "android"
              : "other"
          : undefined
      }
      style={{
        // GPU compositing layer — critical for Instagram WebView position:fixed reliability
        WebkitTransform: "translate3d(0,0,0)",
        transform: "translate3d(0,0,0)",
        // Prevent iOS rubber-band scroll passthrough
        touchAction: "none",
        overscrollBehavior: "none",
        // Explicit max-height for variable-chrome WebViews (+ IG visualViewport)
        maxHeight: embeddedSocialBrowser
          ? "var(--ig-vv-height, 100dvh)"
          : "100dvh",
        height: embeddedSocialBrowser
          ? "var(--ig-vv-height, 100dvh)"
          : undefined,
        // Layout/paint isolation: prevents external reflows from affecting the player
        contain: "layout paint style",
      }}
    >
      {/* ── Progressive blur-up backdrop (gradient → LQIP → full poster).
             LCP CRITICAL: paint poster ASAP with optimistic playback ID (env /
             Worker dualCta / canónico). Do NOT wait for A/B slot (≤3s) or Mux
             API — that black gap was the main driver of LCP p75 ~5.5s on Datadog.
             Player still waits for playerMountGateOpen so it never remounts. ── */}
      {(() => {
        const posterHidden = (videoPlaying && !videoBroken) || videoEnded;
        // Optimistic ID available on first paint; swap to final when gate opens.
        const posterMuxId = (
          dualCtaCfg?.posterPlaybackId ||
          (playerMountGateOpen && effectivePlaybackId
            ? effectivePlaybackId
            : null) ||
          dualCtaCountryMux ||
          ctMux ||
          ENV_PLAYBACK_ID ||
          brandMuxFallback ||
          CANONICAL_MUX_PLAYBACK_ID ||
          ""
        ).trim();
        if (!posterMuxId && !dualCtaCfg?.posterImageUrl) return null;
        // Prioridad del poster por país (/admin/country-template):
        // 1) posterImageUrl (imagen propia https) → pisa todo.
        // 2) posterPlaybackId / video resuelto (frame Mux).
        if (dualCtaCfg?.posterImageUrl) {
          return (
            <img
              key={`poster-img-${dualCtaCfg.posterImageUrl}`}
              src={dualCtaCfg.posterImageUrl}
              alt=""
              aria-hidden
              fetchPriority="high"
              decoding="async"
              className={cn(
                "absolute inset-0 z-[8] h-full w-full object-cover transition-opacity duration-300 ease-out",
                posterHidden ? "opacity-0" : "opacity-100",
              )}
              onLoad={() => setPosterReady(true)}
              onError={() => setPosterReady(true)}
            />
          );
        }
        return (
          <MuxPosterBackdrop
            key={`poster-${posterMuxId}`}
            className="z-[8]"
            playbackId={posterMuxId}
            // Frame del poster (posterTime) sobre el video que reproduce, o sobre
            // posterPlaybackId si el admin definió un video distinto para el poster.
            time={dualCtaCfg?.posterTime ?? 0}
            fitMode="smartcrop"
            hidden={posterHidden}
            onReady={() => setPosterReady(true)}
          />
        );
      })()}

      {/* ── Meta/IG IAB lightweight: poster + tap-to-play (no Mux autoplay/HLS until gesture).
             Reduces black-frame stalls + data use in Instagram WebView. ── */}
      {/* Meta IAB: open-in-browser chip — Stripe/cookies more reliable outside WebView */}
      {playerMountGateOpen && embeddedSocialBrowser && (
        <div
          className={cn(
            "dual-cta-iab-open-browser absolute left-0 right-0 top-0 z-[120] flex justify-center px-3 pt-[max(0.5rem,env(safe-area-inset-top))]",
            "pointer-events-none",
          )}
        >
          <button
            type="button"
            className={cn(
              "pointer-events-auto mt-1 rounded-full border border-white/25 bg-black/65 px-3 py-1.5",
              "text-[11px] font-medium text-white/95 backdrop-blur-md",
              "instagram-browser-buttons shadow-lg",
            )}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            onClick={() => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ph = (window as any).posthog;
                ph?.capture?.("dual_cta_iab_open_system_browser", {
                  site: BRAND_SOURCE_KEY,
                  source: BRAND_SOURCE_KEY,
                  country: effectiveCountry,
                  is_instagram_app: metaIabInfo.isInstagramApp,
                  platform: metaIabInfo.platform,
                  has_checkout_resume: Boolean(iabCheckoutResumeUrl),
                });
              } catch {
                /* */
              }
              openUrlInSystemBrowserFromIab(
                iabCheckoutResumeUrl || window.location.href,
              );
            }}
          >
            {muxLang === "es"
              ? iabCheckoutResumeUrl
                ? "Abrí Safari/Chrome · completar pago"
                : "Abrir en el navegador · mejor pago"
              : iabCheckoutResumeUrl
                ? "Open Safari/Chrome · finish payment"
                : "Open in browser · smoother checkout"}
          </button>
        </div>
      )}

      {/* Sticky resume after VIP breakout if still inside IG WebView */}
      {embeddedSocialBrowser &&
        iabCheckoutResumeUrl &&
        isIabResumeBannerEnabled(opsConfig) && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[130] flex justify-center px-3"
          style={{
            paddingBottom: getMetaIabCtaBottomInsetCss(metaIabInfo),
          }}
        >
          <button
            type="button"
            className={cn(
              "pointer-events-auto mb-2 w-full max-w-sm rounded-full",
              "bg-amber-400 px-5 py-3.5 text-sm font-bold text-black shadow-lg",
              "active:scale-[0.97] transition-transform duration-150 ease-out",
              "instagram-browser-buttons",
            )}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent", minHeight: 52 }}
            onClick={() => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).posthog?.capture?.("dual_cta_iab_resume_checkout", {
                  site: BRAND_SOURCE_KEY,
                  source: BRAND_SOURCE_KEY,
                  country: effectiveCountry,
                });
              } catch {
                /* */
              }
              openUrlInSystemBrowserFromIab(iabCheckoutResumeUrl);
            }}
          >
            {muxLang === "es"
              ? "Continuar el pago en el navegador →"
              : "Continue payment in browser →"}
          </button>
        </div>
      )}

      {playerMountGateOpen &&
        effectivePlaybackId &&
        !conversionCtaFirst &&
        lightweightIabVideo &&
        !iabUserRequestedPlay && (
          <button
            type="button"
            className={cn(
              // Leave bottom clear so VIP CTA (z-110) stays tappable — IG chrome + CTA stack
              "dual-cta-iab-tap-play absolute left-0 right-0 top-0 z-[13] flex flex-col items-center justify-center gap-3",
              "bg-black/20 touch-manipulation",
              embeddedSocialBrowser && "instagram-browser-buttons",
            )}
            style={{ bottom: "9.5rem" }}
            aria-label={
              muxLang === "es" ? "Reproducir video" : "Play video"
            }
            onClick={() => {
              setIabUserRequestedPlay(true);
              setVipCtaWaitingHint(true);
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ph = (window as any).posthog;
                ph?.capture?.("dual_cta_iab_tap_to_play", {
                  site: BRAND_SOURCE_KEY,
                  source: BRAND_SOURCE_KEY,
                  country: effectiveCountry,
                  lightweight_iab_video: true,
                  is_instagram_app: metaIabInfo.isInstagramApp,
                  is_meta_iab: true,
                  platform: metaIabInfo.platform,
                });
              } catch {
                /* */
              }
            }}
          >
            <span
              className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-black shadow-lg ring-1 ring-black/10"
              aria-hidden
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            <span className="rounded-full bg-black/55 px-3 py-1 text-[12px] font-medium text-white/95 backdrop-blur-sm">
              {muxLang === "es" ? "Toca para ver el video" : "Tap to watch"}
            </span>
            <span className="max-w-[16rem] text-center text-[11px] font-medium text-white/80">
              {muxLang === "es"
                ? "O unite como miembro abajo — sin esperar el video"
                : "Or join as a member below — no need to wait"}
            </span>
          </button>
        )}

      {/* ── Video player — deferred until slot resolves so effectivePlaybackId is
             final and the player never remounts due to A/B slot arriving late.
             Meta IAB lightweight: mount only after user tap (no autoplay). ── */}
      {playerMountGateOpen &&
        effectivePlaybackId &&
        !conversionCtaFirst &&
        (!lightweightIabVideo || iabUserRequestedPlay) && (
      <div className="absolute inset-0 z-[12] h-full w-full">
      <MuxVideoPlayerWithCTA
        key={`player-session-${playerRemountKey}`}
        playbackId={effectivePlaybackId}
        countryCode={effectiveCountry}
        forceManualPlaybackId={true}
        parentOwnsPlaybackRotation={true}
        fallbackPlaybackIds={muxPlaybackAlternatives}
        language={muxLang}
        displayMode="cover"
        containerClassName={
          embeddedSocialBrowser ? "instagram-browser-container h-full w-full" : "h-full w-full"
        }
        // Loop configurable por país (/admin/country-template); default false.
        loop={dualCtaCfg?.videoLoop === true}
        // Meta IAB lightweight: no mount until tap; after tap use muted autoplay
        // so the same user gesture chain starts playback without a second tap.
        // Non-lightweight Meta edge: muted-first. Desktop: any.
        autoPlay={
          lightweightIabVideo
            ? "muted"
            : embeddedSocialBrowser
              ? "muted"
              : "any"
        }
        preferAutoplayWithSound={!embeddedSocialBrowser && !lightweightIabVideo}
        hideUnmutePrompt={false}
        fullViewportOnMobile={true}
        // Mux Data attribution (analytics only, no visible title)
        muxDataVideoTitle={DUAL_CTA_TEASER_TITLE}
        muxDataVideoSeries={DUAL_CTA_TEASER_EXTERNAL_ID}
        // No service/ctaUrl/ctaLabel → player shows video only, no bottom CTA button
        skipWorkerTemplateCta={true}
        externalCheckoutCta={true}
        // Engagement mid-roll A/B: only set when arm is gate_hotspot
        pauseAtSeconds={
          engagementGateArm === "gate_hotspot" ? pauseAtSeconds : null
        }
        onPausedAtGate={(payload) => {
          setEndedViaEngagementGate(true);
          setVideoEnded(true);
          setVideoPlaying(false);
          maxPlayheadSecondsRef.current = Math.max(
            maxPlayheadSecondsRef.current,
            payload.currentTime,
          );
          if (payload.durationSeconds != null && payload.durationSeconds > 0) {
            videoDurationSecondsRef.current = payload.durationSeconds;
          }
          trackDualCta("dual_cta_engagement_gate_hit", {
            playback_id: effectivePlaybackId,
            current_time: payload.currentTime,
            gate_seconds: payload.gateSeconds,
            duration_seconds: payload.durationSeconds,
            ...engagementGateTelemetryProps({
              arm: engagementGateArm,
              gateSource: engagementGateMetaRef.current.gate_source,
              gateSeconds: payload.gateSeconds,
              hotspotScore: engagementGateMetaRef.current.hotspot_score,
              muxTotalViews: engagementGateMetaRef.current.mux_total_views,
              assignmentSource: engagementGateMetaRef.current.assignment_source,
              rolloutPct: engagementGateMetaRef.current.rollout_pct,
            }),
            ...getVideoWatchTelemetryFields(payload.currentTime, false),
            embedded_social_browser: embeddedSocialBrowser,
          });
          // Treat as paywall surface for conversion funnel continuity
          trackDualCta("dual_cta_video_ended", {
            ended_via_engagement_gate: true,
            ...getVideoWatchTelemetryFields(payload.currentTime, false),
            embedded_social_browser: embeddedSocialBrowser,
          });
        }}
        // El poster + loading indicator los maneja DualCTAVideoLanding (arriba).
        // Suprime shimmer/heavy loader del player para evitar flash doble capa.
        hideLoadingShimmer={true}
        onReady={() => setVideoReady(true)}
        onPlay={() => {
          setVideoReady(true);
          // Meta IAB: do NOT hide poster on `play` alone — Mux defers the
          // player at opacity:0 until `playing`/reveal. Hiding the poster early
          // caused a black frame (replay 019fa96f). Wait for onPlaying/playhead.
          if (!embeddedSocialBrowser) {
            markDualCtaVideoStarted("play");
          }
        }}
        onPlaybackTelemetry={(payload) => {
          // Mux currentTime is the source of truth for "how long they watched".
          if (
            Number.isFinite(payload.currentTimeSeconds) &&
            payload.currentTimeSeconds > maxPlayheadSecondsRef.current
          ) {
            maxPlayheadSecondsRef.current = payload.currentTimeSeconds;
            if (payload.currentTimeSeconds > 0.15) {
              markDualCtaVideoStarted("playhead");
            }
          }
          if (
            payload.durationSeconds != null &&
            Number.isFinite(payload.durationSeconds) &&
            payload.durationSeconds > 0
          ) {
            videoDurationSecondsRef.current = payload.durationSeconds;
            // Once player duration is known: re-fetch gate with duration so
            // short teasers (~14–17s) get minGate < 8s and heatmap % is correct.
            if (
              engagementGateArm === "gate_hotspot" &&
              !endedViaEngagementGate &&
              !engagementGateMetaRef.current.duration_refined
            ) {
              engagementGateMetaRef.current.duration_refined = true;
              const pid = String(effectivePlaybackId || "").trim();
              if (pid.length >= 10) {
                void fetchMuxEngagementGate(pid, {
                  timeframe: "7:days",
                  durationSeconds: payload.durationSeconds,
                }).then((body) => {
                  if (!body?.gate || endedViaEngagementGate) return;
                  let startSec =
                    body.gate.start_seconds != null &&
                    Number.isFinite(body.gate.start_seconds)
                      ? body.gate.start_seconds
                      : pauseAtSeconds ?? 18;
                  startSec =
                    clampGateSeconds(startSec, payload.durationSeconds) ??
                    startSec;
                  if (
                    pauseAtSeconds == null ||
                    Math.abs(startSec - pauseAtSeconds) > 0.35
                  ) {
                    setPauseAtSeconds(startSec);
                    engagementGateMetaRef.current.gate_seconds = startSec;
                    engagementGateMetaRef.current.gate_source =
                      body.gate.source ??
                      engagementGateMetaRef.current.gate_source;
                    trackDualCta("dual_cta_engagement_gate_resolved", {
                      playback_id: pid,
                      duration_seconds: payload.durationSeconds,
                      refined_with_player_duration: true,
                      ...engagementGateTelemetryProps({
                        arm: "gate_hotspot",
                        gateSource: body.gate.source,
                        gateSeconds: startSec,
                        hotspotScore: body.gate.score ?? null,
                        muxTotalViews: body.total_views ?? 0,
                        assignmentSource:
                          engagementGateMetaRef.current.assignment_source,
                        rolloutPct: engagementGateMetaRef.current.rollout_pct,
                      }),
                    });
                  }
                });
              }
            } else if (
              engagementGateArm === "gate_hotspot" &&
              pauseAtSeconds != null &&
              !endedViaEngagementGate
            ) {
              // Already refined: light clamp only (85% max)
              const refined = clampGateSeconds(
                pauseAtSeconds,
                payload.durationSeconds,
              );
              if (refined != null && Math.abs(refined - pauseAtSeconds) > 0.5) {
                setPauseAtSeconds(refined);
                engagementGateMetaRef.current.gate_seconds = refined;
              }
            }
          }
          // On end, pin playhead to duration so watch == full video.
          if (payload.reason === "ended" && payload.durationSeconds != null) {
            maxPlayheadSecondsRef.current = Math.max(
              maxPlayheadSecondsRef.current,
              payload.durationSeconds,
            );
          }
        }}
        onPlaying={() => {
          markDualCtaVideoStarted("playing");
        }}
        onEnded={() => {
          // Natural end (control arm, or gate never fired)
          if (!endedViaEngagementGate) {
            setEndedViaEngagementGate(false);
          }
          setVideoEnded(true);
          const watchedSec = getVideoWatchedSeconds();
          // If playhead never reported, approximate full watch from wall-clock.
          if (maxPlayheadSecondsRef.current < 0.25 && videoStartTimeRef.current) {
            const wall = Math.round((Date.now() - videoStartTimeRef.current) / 1000);
            maxPlayheadSecondsRef.current = Math.max(maxPlayheadSecondsRef.current, wall);
          }
          trackDualCta("dual_cta_video_ended", {
            ended_via_engagement_gate: endedViaEngagementGate,
            ...getVideoWatchTelemetryFields(getVideoWatchedSeconds() || watchedSec, true),
            embedded_social_browser: embeddedSocialBrowser,
          });
          trackLeadMeta({
            content_name: "Teaser Video Completed",
            content_category: "DualCTA Video Landing",
          });
        }}
        onError={(message) => {
          const msgStr = String(message ?? "");
          const isInvalidPlaybackId =
            /URL or playback-id does not exist|may have used an Asset ID|Authorization error trying to access this video URL/i.test(
              msgStr,
            );
          const isDemuxerOrDecodeError =
            /demuxer|could_not_parse|decode|media_err_decode/i.test(msgStr);
          // Red/CDN caído o transitorio (el player ya agotó sus retries con
          // backoff antes de delegar acá — ver MuxVideoPlayerWithCTA).
          const isNetworkError =
            /network|connection|tunnel|manifestloaderror|media download|failed to fetch|timeout|loaderror|hlserror|fragloaderror/i.test(
              msgStr,
            );
          // Mainland China: stream.mux.com often blocked/unstable (GFW). Prefer
          // CDN classification over "revisá Playback ID" so ops don't chase admin.
          const isChinaVisitor =
            String(effectiveCountry || "").toUpperCase() === "CN";
          let muxIssueKind = "mux_fatal_onerror";
          if (isInvalidPlaybackId) {
            muxIssueKind = "mux_invalid_playback_id";
          } else if (isChinaVisitor) {
            // CN + non-invalid: treat as regional CDN/network (not missing admin config)
            muxIssueKind = "mux_cdn_region_cn";
          } else if (isNetworkError) {
            muxIssueKind = "mux_fatal_network";
          } else if (isDemuxerOrDecodeError) {
            muxIssueKind = "mux_fatal_decode";
          }
          const nextPlaybackId = pickNextPlaybackId(effectivePlaybackId);
          const willRotateToAlternate = Boolean(nextPlaybackId);
          const isOnHardcodedFallback =
            Boolean(brandMuxFallback) && effectivePlaybackId === brandMuxFallback;
          // 2026-07-16: el swap a brand fallback aplicaba SOLO a playback IDs
          // inválidos — un error de red con la rotación agotada iba directo a
          // videoBroken sin intentar el fallback (PostHog: "Video falló —
          // abandonó por bug"). Red y decode ahora también swapean.
          const willSwapToBrandFallback =
            (isInvalidPlaybackId || isNetworkError || isDemuxerOrDecodeError) &&
            !willRotateToAlternate &&
            Boolean(hardFallbackPlaybackId.trim()) &&
            hardFallbackPlaybackId !== effectivePlaybackId &&
            !failedPlaybackIdsRef.current.has(hardFallbackPlaybackId) &&
            !isOnHardcodedFallback &&
            !forceFallbackPlaybackId;
          const willRetryChainForNetwork =
            isNetworkError &&
            !willRotateToAlternate &&
            !willSwapToBrandFallback &&
            networkChainRetryCountRef.current < 1;
          const isRecoverableError =
            willRotateToAlternate || willSwapToBrandFallback;
          trackDualCta("dual_cta_video_error", {
            message,
            error_count: videoErrorCountRef.current + 1,
            fatal: !isRecoverableError,
            seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
            video_started: hasDualCtaVideoStarted(),
            embedded_social_browser: embeddedSocialBrowser,
            playback_id: effectivePlaybackId,
            using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
            is_demuxer_error: isDemuxerOrDecodeError,
            is_network_error: isNetworkError,
            will_rotate_playback_id: willRotateToAlternate,
            next_playback_id: nextPlaybackId?.slice(-8),
            will_swap_to_fallback: willSwapToBrandFallback,
            will_retry_chain: willRetryChainForNetwork,
          });
          if (willRotateToAlternate && nextPlaybackId) {
            clearCachedPlaybackId();
            setVideoBroken(false);
            setVideoPlaying(false);
            setVideoReady(false);
            setVideoEnded(false);
            setPlaybackIdOverride(nextPlaybackId);
            setPlayerRemountKey((k) => k + 1);
            return;
          }
          if (willSwapToBrandFallback) {
            clearCachedPlaybackId();
            // Mismos resets que la rotación: sin ellos el player remonta con
            // videoReady/videoPlaying stale y el overlay de loading no vuelve.
            setVideoBroken(false);
            setVideoPlaying(false);
            setVideoReady(false);
            setVideoEnded(false);
            setPlaybackIdOverride(null);
            setForceFallbackPlaybackId(true);
            setPlayerRemountKey((k) => k + 1);
            return;
          }
          // Fatal from the player (retries/fallback exhausted). Count only here
          // so engagement_dropoff no marca "video_error" por errores recuperables.
          videoErrorCountRef.current += 1;
          setVideoBroken(true);
          setVideoPlaying(false);
          // Último recurso solo-red: un retry diferido de la cadena completa.
          // Mientras tanto videoBroken deja el CTA full-screen (la conversión
          // no depende del video). Si la red volvió (wifi→LTE, CDN blip), el
          // teaser reaparece; si falla de nuevo, el contador bloquea más loops.
          if (willRetryChainForNetwork) {
            networkChainRetryCountRef.current += 1;
            networkChainRetryTimerRef.current = window.setTimeout(() => {
              networkChainRetryTimerRef.current = null;
              failedPlaybackIdsRef.current.clear();
              setPlaybackIdOverride(null);
              setForceFallbackPlaybackId(false);
              setVideoBroken(false);
              setVideoPlaying(false);
              setVideoReady(false);
              setVideoEnded(false);
              setPlayerRemountKey((k) => k + 1);
            }, 8_000);
          }
          // Slack — alert real-time del Mux error (mismo síntoma que el incidente
          // 2026-05 con playback IDs apuntando a otra cuenta). Dedup default
          // 5 min por (kind+template+user) ya lo hace `sendFunnelAlert`.
          if (videoErrorCountRef.current <= 3) {
            postSlackFunnelDualCta(
              "mux_playback_error",
              dualCtaSessionIdRef.current,
              {
                hero_cta: "dual_cta",
                issue_kind: muxIssueKind,
                error_message: String(message ?? "").slice(0, 500),
                error_count: videoErrorCountRef.current,
                seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
                video_started: hasDualCtaVideoStarted(),
                embedded_social_browser: embeddedSocialBrowser,
                using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
                will_swap_to_fallback: willSwapToBrandFallback,
                is_network_error: isNetworkError,
                is_demuxer_error: isDemuxerOrDecodeError,
                is_invalid_playback_id: isInvalidPlaybackId,
                is_china_cdn_likely: muxIssueKind === "mux_cdn_region_cn",
                conversion_possible: true, // full-screen VIP (+ free TG) when videoBroken
                not_a_conversion_dropoff: false,
                dual_cta_ctas_usable: true,
                surface_dual_cta: true,
                ...getLandingEnvironmentFields(),
                ...getActivePlaybackFields(),
              },
            );
          }
          trackDualCtaMuxErrorNoConversion({
            trigger: "fatal_playback",
            pathname: location.pathname,
            country: effectiveCountry,
            errorCount: videoErrorCountRef.current,
            message: msgStr,
            fatal: true,
            videoStarted: hasDualCtaVideoStarted(),
            secondsSinceMount: Math.round((Date.now() - mountTimeRef.current) / 1000),
            playbackId: effectivePlaybackId,
            extra: {
              issue_kind: muxIssueKind,
              is_demuxer_error: isDemuxerOrDecodeError,
              is_network_error: isNetworkError,
              is_invalid_playback_id: isInvalidPlaybackId,
              is_china_cdn_likely: muxIssueKind === "mux_cdn_region_cn",
              embedded_social_browser: embeddedSocialBrowser,
              using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
              conversion_possible: true,
              dual_cta_ctas_usable: true,
            },
          });
        }}
        analyticsFlowSurface={DUAL_CTA_MUX_ANALYTICS_SURFACE}
      />
      </div>
      )}

      {/* ── Loading estable (sin pulse en el CTA de conversión).
             1) Resolviendo ID (slot + Mux): fullscreen loading — CTAs aún no.
             2) Player montado, buffer: pill quieta sobre el poster; CTA sólido abajo. ── */}
      {!videoPlaying && !videoEnded && !videoBroken && (
        <div
          className="pointer-events-none absolute inset-0 z-[20]"
          role="status"
          aria-live="polite"
          aria-busy={!playerMountGateOpen || !videoReady}
        >
          {!playerMountGateOpen ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50">
              <div className="relative h-10 w-10" aria-hidden>
                <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
              </div>
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
                {label("loading", L.loading)}
              </span>
            </div>
          ) : (
            <div
              className="absolute inset-x-0 bottom-0 flex justify-center"
              style={{
                paddingBottom:
                  "max(6.75rem, calc(env(safe-area-inset-bottom, 0px) + 6.75rem))",
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 backdrop-blur-md ring-1 ring-white/10">
                <span className="relative h-3 w-3 shrink-0" aria-hidden>
                  <span className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
                </span>
                <span className="text-[10px] font-medium tracking-wide text-white/75">
                  {label("loading", L.loading)}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Broken video: poster visible, hide frozen demuxer frame ── */}
      {videoBroken && (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-[101] bg-black/55"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 z-[102] flex items-center justify-center">
            <p className="rounded-xl bg-black/60 px-5 py-3 text-center text-sm text-white/70">
              {dualCtaCfg?.videoUnavailableText ||
                (
                  {
                    en: "Video unavailable",
                    es: "Video no disponible",
                    de: "Video nicht verfügbar",
                    fr: "Vidéo non disponible",
                    it: "Video non disponibile",
                    pt: "Vídeo indisponível",
                    nl: "Video niet beschikbaar",
                    tr: "Video kullanılamıyor",
                    ar: "الفيديو غير متاح",
                    pl: "Wideo niedostępne",
                    ja: "動画は利用できません",
                    ko: "동영상을 사용할 수 없습니다",
                    id: "Video tidak tersedia",
                  } as Record<string, string>
                )[muxLang] ||
                "Video unavailable"}
            </p>
          </div>
        </>
      )}

      {/* ── End-state black cover (video finished → focus attention on CTAs) ── */}
      {videoEnded && (
        <div
          className="absolute inset-0 z-[100] bg-black/90 transition-opacity duration-500"
          aria-hidden
        />
      )}

      {/* ── CTA overlay ────────────────────────────────────────────────────
          During playback: only the bottom strip is in the hit-tree so the
          center unmute affordance (player z-12) stays tappable. Full-screen
          only after the teaser ends (focus on CTAs).
          CTAs solo cuando resolutionReady — evita montar/desmontar/parpadeo
          mientras el slot A/B o Mux API aún resuelven el playback. */}
      <div
        className={cn(
          "absolute inset-x-0 flex flex-col pointer-events-none",
          videoEnded || videoBroken ? "inset-y-0" : "bottom-0",
        )}
        style={{ zIndex: 110 }}
      >
        {/* Bottom gradient scrim for legibility during playback */}
        {!videoEnded && playerMountGateOpen && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 [@media(min-height:700px)]:h-52"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.20) 60%, transparent 100%)",
            }}
            aria-hidden
          />
        )}

        {/* CTA region — bottom during playback, centred when video ends.
            Hidden until player mount gate: loading owns the first paint. */}
        {playerMountGateOpen && (
        <div
          className={cn(
            "relative flex justify-center px-5",
            embeddedSocialBrowser && "dual-cta-iab-cta-stack",
            videoEnded || videoBroken
              ? "flex-1 items-center"
              : "items-end pt-4",
          )}
          style={
            videoEnded || videoBroken
              ? embeddedSocialBrowser
                ? {
                    paddingBottom: getMetaIabCtaBottomInsetCss(metaIabInfo),
                    paddingTop: "max(3.5rem, env(safe-area-inset-top, 0px))",
                  }
                : undefined
              : {
                  paddingBottom: getMetaIabCtaBottomInsetCss(metaIabInfo),
                }
          }
        >
          <div
            className={cn(
            "pointer-events-auto flex w-full max-w-sm flex-col items-stretch",
            embeddedSocialBrowser && "instagram-browser-buttons",
            videoEnded ? "gap-6" : embeddedSocialBrowser ? "gap-3" : "gap-5",
            // Sin ring/recuadro alrededor del stack de CTAs (pedido UX + replay MX/IG).
          )}
          >
            {/* Meta IAB: always nudge VIP while video not started (MX/CL paid path) */}
            {(vipCtaWaitingHint ||
              ctaScrollHint ||
              (embeddedSocialBrowser && !videoPlaying && !videoEnded)) &&
              showVipCta &&
              !videoPlaying &&
              !videoEnded && (
              <p className="text-center text-[12px] font-medium leading-snug text-white/90">
                {embeddedSocialBrowser
                  ? muxLang === "en"
                    ? "Join now — video optional in Instagram"
                    : "Unite ahora — el video es opcional en Instagram"
                  : dualCtaCfg?.loadingJoinHint ||
                    (muxLang === "en"
                      ? "Video is loading — you can join now"
                      : "El video está cargando — podés unirte ahora")}
              </p>
            )}
            {/* Mid-roll membership gate — objetivo: captar miembro VIP */}
            {videoEnded && endedViaEngagementGate && showVipCta && (
              <p
                className="text-center text-[15px] font-semibold leading-snug text-white tracking-tight"
                data-ph-section="dual_cta_engagement_gate_copy"
              >
                {muxLang === "en"
                  ? opsConfig?.engagement_gate_copy_en ||
                    "Join as a member to keep watching — special first-month offer"
                  : opsConfig?.engagement_gate_copy_es ||
                    "Unite como miembro y seguí mirando — oferta especial el primer mes"}
              </p>
            )}
            {checkoutReturnError && (
              <p
                className="rounded-xl bg-red-950/80 px-4 py-3 text-center text-[13px] leading-snug text-red-200 ring-1 ring-red-500/30"
                role="alert"
              >
                {checkoutReturnError}
              </p>
            )}
            {checkoutStarting && isExteriorDualCta && vipPriceOffer && (
              <p
                className="rounded-xl bg-black/70 px-4 py-2.5 text-center text-[12px] leading-snug text-amber-100 ring-1 ring-amber-400/30"
                role="status"
                aria-live="polite"
              >
                {muxLang === "es" ? (
                  <>
                    Llevás la promo{" "}
                    <strong className="text-amber-200">{vipPriceOffer.badge}</strong>
                    {" — "}
                    <strong className="tabular-nums">{vipPriceOffer.firstMonth}</strong> el
                    primer mes
                  </>
                ) : (
                  <>
                    Promo{" "}
                    <strong className="text-amber-200">{vipPriceOffer.badge}</strong>
                    {" — "}
                    <strong className="tabular-nums">{vipPriceOffer.firstMonth}</strong>{" "}
                    first month
                  </>
                )}
              </p>
            )}

            {/* Oferta 1er mes / precio list — controlable desde ops (captación) */}
            {vipPriceOffer &&
              showVipCta &&
              !isArVipMercadoPago &&
              (opsConfig?.show_first_month_offer !== false ||
                opsConfig?.show_vip_price_badge !== false) && (
              <div className="text-center space-y-1">
                {isExteriorDualCta && opsConfig?.show_first_month_offer !== false && (
                  <span
                    className="inline-flex items-center rounded-full bg-amber-400/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-200 ring-1 ring-amber-300/40"
                    data-ph-capture-attribute-promo-badge="exterior_first_month"
                  >
                    {vipPriceOffer.badge}{" "}
                    {muxLang === "es" ? "primer mes" : "first month"}
                  </span>
                )}
                {opsConfig?.show_vip_price_badge !== false && (
                  <p className="text-[13px] leading-snug text-white/90">
                    {opsConfig?.show_first_month_offer !== false ? (
                      <>
                        <span className="line-through opacity-50 mr-1.5">
                          {vipPriceOffer.full}
                        </span>
                        <span className="font-bold text-amber-200 tabular-nums">
                          {vipPriceOffer.firstMonth}
                        </span>
                        <span className="text-white/60">
                          {muxLang === "es" ? " el primer mes" : " first month"}
                        </span>
                      </>
                    ) : (
                      <span className="font-bold text-amber-200 tabular-nums">
                        {vipPriceOffer.full}
                        <span className="ml-1 font-normal text-white/55">
                          {muxLang === "es" ? "/mes" : "/mo"}
                        </span>
                      </span>
                    )}
                  </p>
                )}
                {opsConfig?.show_first_month_offer !== false && (
                  <p className="text-[10px] text-white/50">
                    {muxLang === "es"
                      ? `Luego ${vipPriceOffer.full}/mes · cancelás cuando quieras`
                      : `Then ${vipPriceOffer.full}/mo · cancel anytime`}
                  </p>
                )}
                {showStripeTrustBadge && (
                  <div className="mt-0.5">
                    <img
                      src={BRAND_POWERED_BY_STRIPE_URL}
                      alt="Pago seguro procesado por Stripe"
                      className="mx-auto h-2.5 opacity-70 md:h-3"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}
            {/* AR: precio full arriba del CTA (logo MP solo debajo del botón — 1×). */}
            {showVipCta &&
              isArVipMercadoPago &&
              vipPriceOffer &&
              opsConfig?.show_vip_price_badge !== false && (
              <div className="text-center">
                <p className="text-[12px] font-semibold tabular-nums text-amber-200/95">
                  {vipPriceOffer.full}
                  <span className="ml-1 font-normal text-white/55">
                    {muxLang === "es" ? "/mes" : "/mo"}
                  </span>
                </p>
              </div>
            )}

            {/* Primary VIP + trust logo — SIEMPRE primero salvo freeCtaFirst.
                Soft-stall / video end: VIP manda la conversión; free es secundario. */}
            {showVipCta && (
              <div
                className="flex w-full flex-col items-stretch gap-1.5"
                style={{
                  // Orden por país: gratis-first → VIP+trust va segundo.
                  order: freeCtaFirstActive ? 2 : 1,
                }}
              >
                {vipCheckoutGateway === "stripe_subscription" &&
                !vipHrefOverride &&
                !geoCommunityVipHref ? (
                  <button
                    type="button"
                    onClick={handlePremiumCtaActivate}
                    disabled={checkoutStarting}
                    aria-busy={checkoutStarting}
                    className={cn(
                      "group flex w-full flex-col items-center justify-center gap-0.5 rounded-full",
                      "bg-amber-400/95 hover:bg-amber-300 active:scale-[0.97] active:bg-amber-500",
                      "text-black text-sm font-semibold tracking-tight",
                      conversionBoostVip ? "px-6 py-3.5 text-base" : "px-5 py-3",
                      conversionBoostVip
                        ? "shadow-[0_4px_24px_rgba(251,191,36,0.45)] hover:shadow-[0_6px_28px_rgba(251,191,36,0.55)]"
                        : "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                      "transition-[transform,background-color,box-shadow] duration-150 ease-out",
                      "border-0 cursor-pointer disabled:opacity-80 disabled:active:scale-100",
                    )}
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      minHeight: 48,
                    }}
                  >
                    <span className="flex w-full items-center justify-center gap-2">
                      <span className="truncate">
                        {checkoutStarting
                          ? muxLang === "es"
                            ? "Abriendo pago seguro…"
                            : "Opening secure checkout…"
                          : label("vipLabel", L.joinVip)}
                      </span>
                      {!checkoutStarting && (
                        <span
                          className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                          aria-hidden
                        >
                          →
                        </span>
                      )}
                    </span>
                    {isExteriorDualCta && vipPriceOffer && !checkoutStarting && (
                      <span className="text-[11px] font-bold tabular-nums text-black/75">
                        {vipPriceOffer.firstMonth}
                        {muxLang === "es" ? " el 1.er mes" : " first month"}
                        <span className="font-semibold opacity-70">
                          {" "}
                          · {vipPriceOffer.badge}
                        </span>
                      </span>
                    )}
                  </button>
                ) : (
                  <a
                    href={primaryCheckoutHref}
                    onClick={handlePremiumCtaActivate}
                    className={cn(
                      "group flex w-full items-center justify-center gap-2 rounded-full",
                      "bg-amber-400/95 hover:bg-amber-300 active:scale-[0.97] active:bg-amber-500",
                      "text-black text-sm font-semibold tracking-tight",
                      conversionBoostVip ? "px-6 py-4 text-base" : "px-5 py-3.5",
                      conversionBoostVip
                        ? "shadow-[0_4px_24px_rgba(251,191,36,0.45)] hover:shadow-[0_6px_28px_rgba(251,191,36,0.55)]"
                        : "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                      "transition-[transform,background-color,box-shadow] duration-150 ease-out",
                      "no-underline",
                    )}
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      minHeight: 48,
                    }}
                  >
                    <span className="truncate">{label("vipLabel", L.joinVip)}</span>
                    <span
                      className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                      aria-hidden
                    >
                      →
                    </span>
                  </a>
                )}

                {showStripeTrustBadge && (
                  <div className="flex justify-center" aria-label="Pago seguro con Stripe">
                    <img
                      src={BRAND_POWERED_BY_STRIPE_URL}
                      alt="Pago seguro procesado por Stripe"
                      className="h-3.5 opacity-80 md:h-4"
                      loading="lazy"
                    />
                  </div>
                )}
                {/* Logo MP debajo del botón VIP (AR) — visible y con fondo blanco */}
                {showMercadoPagoTrustBadge && (
                  <div
                    className="flex justify-center rounded-xl bg-white px-4 py-2 shadow-md shadow-black/25"
                    aria-label="Pago seguro con Mercado Pago"
                    data-ph-capture-attribute-mp-logo="dual_cta_vip"
                  >
                    <img
                      src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                      alt="Mercado Pago — pago seguro"
                      className="h-7 w-auto max-w-[min(100%,200px)] object-contain md:h-8"
                      loading="eager"
                      draggable={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Free CTA post-video — Clerk /registro cuando admin habilitó free. */}
            {showFreeCtaAfterPlayback && (
              <div
                className="space-y-2.5 rounded-2xl border border-white/12 bg-white/[0.06] px-3 py-3"
                data-ph-section="dual_cta_free_clerk_register"
                style={{
                  order: freeCtaFirstActive ? 1 : 2,
                }}
              >
                <p className="text-center text-[12px] font-medium leading-snug text-white/80">
                  {muxLang === "es"
                    ? "Registrate gratis. Quedás pendiente de aprobación para entrar al portal."
                    : "Register for free. You stay pending approval until portal access."}
                </p>
                <button
                  type="button"
                  data-attr="dual_cta_free_registro"
                  onClick={handleFreeJoinClick}
                  disabled={freeJoinBusy}
                  className={cn(
                    "group flex w-full items-center justify-center gap-2 rounded-full",
                    "bg-white text-black text-sm font-semibold tracking-tight",
                    "px-5 py-3.5",
                    "shadow-[0_2px_16px_rgba(255,255,255,0.12)]",
                    "ring-1 ring-white/30",
                    "transition-[transform,opacity,background-color] duration-150 ease-out",
                    "hover:bg-white/95 active:scale-[0.97]",
                    "disabled:pointer-events-none disabled:opacity-70",
                  )}
                  style={{
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    minHeight: 48,
                  }}
                >
                  {freeJoinBusy ? (
                    <>
                      <span
                        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-black/20 border-t-black"
                        aria-hidden
                      />
                      <span className="truncate">
                        {muxLang === "es" ? "Continuando…" : "Continuing…"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="truncate">{label("freeLabel", L.freePlanCta)}</span>
                      <span
                        className="text-[11px] opacity-60 transition-transform duration-150 group-hover:translate-x-0.5"
                        aria-hidden
                      >
                        →
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Free durante el video — oculto si freeCtaLocked (1 funnel VIP). */}
            {showFreeCtaDuringPlayback && (
              <button
                type="button"
                data-attr="dual_cta_free_registro"
                onClick={() => { void handleFreeJoin(); }}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-full",
                  "bg-white/10 hover:bg-white/15 active:bg-white/20",
                  "backdrop-blur-md",
                  "text-white text-sm font-medium tracking-tight",
                  "px-4 py-3",
                  "ring-1 ring-white/20 hover:ring-white/30",
                  "ui-transition duration-150",
                )}
                style={{
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  minHeight: 44,
                  // Orden por país: gratis-first → el CTA gratis va primero.
                  order: freeCtaFirstActive ? 1 : 2,
                }}
              >
                <span className="truncate">{label("freeLabel", L.freePlanCta)}</span>
              </button>
            )}

            {/* Free LOCKED: never render grey free button — VIP only funnel. */}
          </div>
        </div>
        )}
      </div>

      {/* ── Embedded Checkout (flag PostHog) — modal sobre el video ── */}
      {embeddedCheckoutSecret && (
        <Suspense fallback={null}>
          <StripeEmbeddedCheckoutModal
            clientSecret={embeddedCheckoutSecret}
            onClose={() => {
              setEmbeddedCheckoutSecret(null);
              trackDualCta("dual_cta_embedded_checkout_closed", {
                ...getPageContext(),
              });
            }}
            onError={(message) => {
              // Mount falló (Stripe.js bloqueado, red, etc.) → hosted de siempre.
              setEmbeddedCheckoutSecret(null);
              trackDualCta("dual_cta_embedded_checkout_fallback", {
                error_message: message.slice(0, 200),
                ...getPageContext(),
              });
              const args = lastVipCheckoutArgsRef.current;
              if (args) {
                void startVipMembershipVideoCheckout({
                  ...args,
                  preferEmbedded: false,
                  // El intento ya se trackeó en el click original (initiated /
                  // template_used / markDualCtaConverted); la sesión hosted nueva
                  // es intencional (el usuario necesita un checkout), solo se
                  // suprime el tracking duplicado.
                  onIntent: undefined,
                });
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
